import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Constants, createCameraVideoTrack } from "../../config/sdkConfig";
import { stringToNumericUid } from "../../context/AgoraContext";
import { ChevronDown, User, LogOut, LayoutDashboard, Monitor, MonitorOff, Coins, Clock, Sparkles, Mic } from "lucide-react";
import RewardsDropdownPanel from "../../components/studio/rewards/RewardsPanel";
import rewardService from "../../services/rewardService";
import RLinkLogo from "../../components/common/RLinkLogo";
import { useWhiteLabel } from "../../context/WhiteLabelContext";
import { useSiteContext } from "../../context/SiteContext";
import { sideBarModes } from "../../utils/common";
import SettingsPanel from "./SettingsPanel";
import MobileBottomBar from "./MobileBottomBar";
import MobileSwipeUpPanel from "./MobileSwipeUpPanel";
import CaptionTranslationPanel from "./CaptionTranslationPanel";
import { backgroundImageUrls } from "../../utils/background";
import { emojis } from "../../utils/list";
import ToolsDropdown from "./ToolsDropdown";
import { useTopBar } from "../../hooks/useTopBar";
import * as meetingServices from "../../services/meetingServices";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useBreakoutRooms } from "../../context/BreakoutRoomContext";
import { useMeetingState } from "../../context/MeetingStateContext";
import { Lock } from "lucide-react";
import PrivacyNoticeModal, { PRIVACY_NOTICE_MESSAGE } from "./PrivacyNoticeModal";

import RecordingLayoutPicker from "./RecordingLayoutPicker";

const TopBar = (props) => {
  const {
    // Context values
    meetingRole,
    user,
    isAuthenticated,
    meetingSettings,
    showElementSection,
    setShowElementSection,
    selectedLayout,
    setSelectedLayout,
    hideSelfView,
    setHideSelfView,
    showNonVideoParticipants,
    setShowNonVideoParticipants,
    videoResolution,
    handleVideoResolution,
    translationStatus,
    showPollsModal,
    setShowPollsModal,
    polls,
    selectedPoll,
    setSelectedPoll,
    loadingPolls,
    showPollResultsModal,
    setShowPollResultsModal,
    pollResults,
    meetingSession,
    setMeetingSettings,

    // Meeting app context
    selectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    setSideBarMode,
    selectedWebcam,
    setUnreadChat,
    unreadChat,
    recentReactions,
    selectedLanguage,
    setSelectedLanguage,
    setTranslationEnabled,
    translationEnabled,
    setTranscriptionEnabled,
    transcriptionEnabled,
    virtualBackgroundOn,
    approvalSoundEnabled,
    setApprovalSoundEnabled,
    showRecordingConsentModal,
    setShowRecordingConsentModal,
    participantApproveFunc,

    // Local state
    isMobile,
    isMobileLike,
    isMobileLandscape,
    mics,
    speakers,
    localMicOn,
    changeMic,
    localWebcamOn,
    changeWebcam,
    showMore,
    setShowMore,
    showSetting,
    setShowSetting,
    showVirtualBackgroundModal,
    setShowVirtualBackgroundModal,
    showLeaveModal,
    setShowLeaveModal,
    webcams,
    showReactions,
    setShowReactions,
    showToolsDropdown,
    setShowToolsDropdown,
    showCaptionsDropdown,
    setShowCaptionsDropdown,
    showMicDropdown,
    setShowMicDropdown,
    showSpeakerDropdown,
    setShowSpeakerDropdown,
    showVideoDropdown,
    setShowVideoDropdown,
    showLayoutDropdown,
    setShowLayoutDropdown,
    showMobilePanel,
    showCaptionTranslationPanel,
    setShowCaptionTranslationPanel,
    setShowMobilePanel,
    isFullscreen,
    setIsFullscreen,
    compactMode,
    setCompactMode,
    localMicState,
    setLocalMicState,
    localWebcamState,
    setLocalWebcamState,
    showHelpModal,
    setShowHelpModal,
    helpIssueText,
    setHelpIssueText,
    showProfileConfirmModal,
    setShowProfileConfirmModal,
    showProfileDropdown,
    setShowProfileDropdown,

    // Live streaming
    showLiveStreamModal,
    setShowLiveStreamModal,
    isLiveStreaming,
    liveStreamPlatforms,
    setLiveStreamPlatforms,
    streamingCredentials,
    setStreamingCredentials,
    streamingStartTime,
    streamingElapsedMs,

    // Recording
    showAudioInputDropdown,
    setShowAudioInputDropdown,
    showVideoSourceDropdown,
    setShowVideoSourceDropdown,
    blurEnabled,
    setBlurEnabled,
    blurStrength,
    setBlurStrength,
    selectedBackground,
    showStartRecordingModal,
    setShowStartRecordingModal,
    recordingStartAt,
    recordingElapsedMs,
    recordingCountdown,
    recordingWaitingForStart,
    isRecording,

    // Meeting
    mMeeting,
    startWhiteboard,
    isRequestProcessing,
    resolvedSessionId,

    // PubSub
    publishRaiseHand,
    publishReaction,

    // Handlers
    handleHelpSubmit,
    handleRecordingClick,
    handleConfirmStartRecording,
    handleStopRecording,
    handlePauseRecording,
    handleResumeRecording,
    formatDuration,
    handleStartLiveStream,
    handleStopLiveStream,
    handleLaunchPoll,
    getMics,
    getWebcams,
    handleToggleFullscreen,
    handleCopyRoomLink,
    handleToggleTranscription,
    handleToggleMic,
    handleStartVirtualBackground,
    handleLeaveModal,
    handleLeaveConfirm,
    handleEndForAll,
    handleLeaveCancel,
    handleEmojiClick,
    onTouchStart,
    onTouchMove,
    onTouchEnd,

    // Props passthrough
    startTranscription,
    stopTranscription,
    startTranslation,
    stopTranslation,
    changeTranslationLanguage,
    publishMeetingSettings,
    admitAllWaitingParticipants,
    webcamOn,
    customVideoStream,
    customAudioStream,
    setIsMeetingLeft,

    // Rewards Panel
    showRewardsPanel,
    setShowRewardsPanel,

    // Meeting state (mute controls)
    hostControls,
    emitForceMuteAll,
    emitGrantUnmuteAll,

    // AI Captions
    aiCaptionsEnabled,

    // Privacy mode — gated at the hook level so any Record start path
    // (TopBar buttons, MobileSwipeUpPanel, autoRecordWebinar) shows the
    // notice modal instead of "Connecting Recorder".
    privacyNoticeOpen,
    setPrivacyNoticeOpen,
  } = useTopBar(props);

  // Breakout room timer for top bar
  const { currentBreakoutRoom, timerRemaining } = useBreakoutRooms();

  // Rewards balance for header card
  const [rewardBalance, setRewardBalance] = useState(0);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await rewardService.getBalance();
        if (res.success) {
          setRewardBalance(res.balance.availableBalance || 0);
        }
      } catch (e) {
        // silent - balance stays at 0
      }
    };
    fetchBalance();
  }, []);

  // Refetch balance when rewards panel closes
  useEffect(() => {
    if (!showRewardsPanel) {
      const fetchBalance = async () => {
        try {
          const res = await rewardService.getBalance();
          if (res.success) {
            setRewardBalance(res.balance.availableBalance || 0);
          }
        } catch (e) {}
      };
      fetchBalance();
    }
  }, [showRewardsPanel]);

  // Refetch balance when a rally token is earned in real-time
  useEffect(() => {
    const handleTokenEarned = async () => {
      try {
        const res = await rewardService.getBalance();
        if (res.success) {
          setRewardBalance(res.balance.availableBalance || 0);
        }
      } catch (e) {}
    };
    window.addEventListener('rally-token-earned', handleTokenEarned);
    return () => window.removeEventListener('rally-token-earned', handleTokenEarned);
  }, []);

  // Get branding and presenters from context for styling and permissions
  const { branding, presenters } = useMeetingAppContext();
  const { branding: wlBranding } = useWhiteLabel();
  const { user: siteUser } = useSiteContext();
  const isModeratorPlan = siteUser?.plan === "co-host";
  // NOTE: useMeetingState() must be destructured BEFORE the auto-record
  // effect below — that effect references `webinarStarted` and would hit a
  // TDZ ReferenceError if the destructure happened later in the function.
  const {
    notetaker: notetakerState,
    recording: recordingState,
    emitRecordingPause,
    emitRecordingResume,
    webinarStarted,
    emitWebinarStart,
    emitMeetingEnd,
    emitEndWebinar,
    privacy: privacyMode,
    emitPrivacyUpdate,
  } = useMeetingState();

  // Privacy mode indicator pill is visible to ALL participants (hosts,
  // co-hosts, presenters, attendees). The modal state lives in useTopBar so
  // any Record start path triggers the same notice.
  const [privacyTooltipOpen, setPrivacyTooltipOpen] = useState(false);
  const handleDisablePrivacy = () => {
    emitPrivacyUpdate?.(false);
    setPrivacyNoticeOpen(false);
  };

  // Auto-start recording for webinars with autoRecordWebinar enabled —
  // ONLY after the host has clicked "Start Webinar". Hosts can enter the
  // room early to prep, and we don't want to start recording / billing
  // until they're actually ready to begin. The Start Webinar button itself
  // also fires this when clicked (immediate path), so this effect mainly
  // covers reload-after-start (`webinarStarted` already true from snapshot).
  const autoRecordTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoRecordTriggeredRef.current) return;
    // Host (moderator) only — if co-hosts also fire this on webinarStarted
    // sync, every co-host publishes their own RECORDING_NOTICE in parallel,
    // and viewers race to set recordingStartedById to whichever message
    // arrives last → wrong person gets the REC badge.
    if (
      meetingSettings &&
      meetingSettings.roomType === "Webinar" &&
      meetingRole === "moderator" &&
      meetingSettings.autoRecordWebinar &&
      webinarStarted &&
      handleConfirmStartRecording &&
      mMeeting?.localParticipant?.id &&
      !isRecording
    ) {
      autoRecordTriggeredRef.current = true;
      const timer = setTimeout(() => {
        console.log("[AutoRecord] Starting auto-recording for webinar in focus layout");
        handleConfirmStartRecording('focus');
      }, 1500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingSettings?.autoRecordWebinar, meetingRole, mMeeting?.localParticipant?.id, isRecording, webinarStarted]);
  const isNotetakerActive = !!notetakerState?.isActive;
  const isHostOrCoHost = meetingRole === "moderator" || meetingRole === "co-host";
  // Unify paused state across both mechanisms:
  //   web mode → recordingState.paused (set via Socket.IO from the backend)
  //   worker-recorder mode → mMeeting.isRecordingPaused (set locally after a REST /pause success)
  const isRecordingPaused = !!recordingState?.paused || !!mMeeting?.isRecordingPaused;
  const recordingModeLive = recordingState?.recordingMode || null;
  // Allow pause for host/co-host in either web mode OR worker-recorder mode.
  // `recordingModeLive` is 'web' for Agora web recording; null/undefined in
  // worker-recorder mode (backend doesn't emit recordingMode on socket for
  // the worker path). Excluding 'mix' keeps mix-mode users from seeing a
  // button the backend would reject with 400.
  const canPauseRecording = isHostOrCoHost && isRecording && recordingModeLive !== 'mix';
  // Frozen elapsed snapshot — when paused, show the elapsed time at pause moment instead of advancing.
  const pausedElapsedRef = useRef(0);
  if (!isRecordingPaused) pausedElapsedRef.current = recordingElapsedMs;
  const displayedRecordingElapsedMs = isRecordingPaused ? pausedElapsedRef.current : recordingElapsedMs;
  const handleTogglePauseRecording = async () => {
    if (!canPauseRecording) return;
    // Web mode still goes through Socket.IO so all participants see the
    // paused indicator in real time. Non-web modes use the REST path added
    // in AgoraContext — the backend /pause route dispatches to the right
    // handler based on the recording's in-memory workerRecorder flag.
    const useSocketPath = recordingModeLive === 'web';
    try {
      if (isRecordingPaused) {
        if (useSocketPath) await emitRecordingResume?.();
        else await mMeeting?.resumeRecording?.();
      } else {
        if (useSocketPath) await emitRecordingPause?.();
        else await mMeeting?.pauseRecording?.();
      }
    } catch (err) {
      console.error('[TopBar] pause/resume toggle failed:', err?.message || err);
    }
  };

  // Webinar "Start Webinar" button. Hosts can enter early to prep — clicking
  // this officially starts the webinar: it broadcasts to every host/co-host
  // (so the button hides for everyone), kicks off auto-record if enabled,
  // starts the live stream if creds are configured, and admits everyone
  // currently in the waiting room (if waiting room isn't enforced).
  const isWebinar = meetingSettings?.roomType === "Webinar";
  const showStartWebinarButton = isWebinar && isHostOrCoHost && !webinarStarted;
  const [startingWebinar, setStartingWebinar] = useState(false);
  const handleStartWebinar = async () => {
    if (startingWebinar || webinarStarted) return;
    setStartingWebinar(true);
    try {
      // 1. Broadcast — hides the button on every host/co-host UI immediately
      emitWebinarStart?.();

      // 2. Auto-admit anyone currently in the waiting room (no-op if empty
      //    or if waiting room enforcement is on — admitAllWaitingParticipants
      //    is the same helper the host UI uses for the "Admit All" button).
      try {
        admitAllWaitingParticipants?.();
      } catch (e) {
        console.warn('[StartWebinar] Admit all failed:', e);
      }

      // 3. Auto-start recording if enabled and not already recording.
      //    The 5-second auto-record effect above will also catch this on
      //    reload, but we fire immediately on click for the snappier UX.
      if (
        meetingSettings?.autoRecordWebinar &&
        !isRecording &&
        typeof handleConfirmStartRecording === 'function'
      ) {
        try {
          console.log('[StartWebinar] Starting auto-record (focus layout)');
          handleConfirmStartRecording('focus');
        } catch (e) {
          console.warn('[StartWebinar] Auto-record failed:', e);
        }
      }

      // 4. Auto-start live streaming if any platform creds are configured
      //    and we're not already streaming.
      if (!isLiveStreaming && typeof handleStartLiveStream === 'function') {
        const platforms = {
          youtube: !!(meetingSettings?.streamYouTubeKey && meetingSettings?.streamYouTubeUrl),
          facebook: !!(meetingSettings?.streamFacebookKey && meetingSettings?.streamFacebookUrl),
          linkedin: !!(meetingSettings?.streamLinkedInKey && meetingSettings?.streamLinkedInUrl),
        };
        if (platforms.youtube || platforms.facebook || platforms.linkedin) {
          try {
            console.log('[StartWebinar] Starting live stream:', platforms);
            await handleStartLiveStream(platforms);
          } catch (e) {
            console.warn('[StartWebinar] Live stream failed:', e);
          }
        }
      }
    } finally {
      setStartingWebinar(false);
    }
  };

  // "End Webinar" — boot participants, stop recording, trigger replay emails.
  // Hosts/co-hosts stay in the room.
  const showEndWebinarButton = isWebinar && isHostOrCoHost && webinarStarted;
  const [endingWebinar, setEndingWebinar] = useState(false);
  const handleEndWebinar = async () => {
    if (endingWebinar) return;
    setEndingWebinar(true);
    try {
      // 1. Stop recording if active
      if (isRecording && typeof handleStopRecording === 'function') {
        try {
          console.log('[EndWebinar] Stopping recording');
          await handleStopRecording();
        } catch (e) {
          console.warn('[EndWebinar] Stop recording failed:', e);
        }
      }

      // 2. Emit end-webinar via socket — boots non-hosts, triggers replay emails
      emitEndWebinar?.();

      // 3. Mark ended via HTTP (belt-and-suspenders with the socket handler)
      try {
        const { endWebinar: endWebinarHttp } = await import('../../services/roomServices');
        await endWebinarHttp(meetingSettings?.roomId);
      } catch (e) {
        console.warn('[EndWebinar] HTTP endWebinar failed:', e);
      }
    } finally {
      setEndingWebinar(false);
    }
  };

  // AI Notetaker top-bar dropdown (host/co-host only)
  const [showNotetakerDropdown, setShowNotetakerDropdown] = useState(false);
  const [notetakerMeetingType, setNotetakerMeetingType] = useState("auto");
  const [showBotInParticipants, setShowBotInParticipants] = useState(() => {
    return typeof window !== "undefined" && !!window.__rlinkShowSttBot;
  });
  const [showMeetingTypeOptions, setShowMeetingTypeOptions] = useState(false);
  const notetakerDropdownRef = useRef(null);
  const notetakerBtnRef = useRef(null);
  useEffect(() => {
    if (!showNotetakerDropdown) return;
    const handleClickOutside = (e) => {
      if (notetakerBtnRef.current && notetakerBtnRef.current.contains(e.target)) return;
      if (notetakerDropdownRef.current && notetakerDropdownRef.current.contains(e.target)) return;
      setShowNotetakerDropdown(false);
      setShowMeetingTypeOptions(false);
    };
    const t = setTimeout(() => document.addEventListener("mousedown", handleClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotetakerDropdown]);
  const meetingTypeOptions = [
    { id: "auto", label: "Auto-detect" },
    { id: "sales", label: "Sales Call" },
    { id: "coaching", label: "Coaching Session" },
    { id: "internal", label: "Internal Meeting" },
    { id: "webinar", label: "Webinar" },
    { id: "strategy", label: "Client Strategy" },
    { id: "podcast", label: "Podcast/Content" },
    { id: "other", label: "Other" },
  ];

  useEffect(() => {
    setLocalMicState(mMeeting?.localMicOn);
  }, [mMeeting?.localMicOn]);

  // Toggle Row Components
  const TranscriptionToggleRow = ({ variant = "dark", className = "" }) => {
    const isLight = variant === "light";
    const labelColor = isLight ? "text-slate-800" : "text-white";
    const isActive = aiCaptionsEnabled;
    const switchBackground = isActive
      ? "bg-[#7c3aed] shadow-[0_0_0_4px_rgba(124,58,237,0.25)]"
      : isLight
      ? "bg-slate-300"
      : "bg-gray-600";
    const thumbTransform = isActive
      ? "translate-x-[1.4rem]"
      : "translate-x-1";
    const thumbStyles = isActive
      ? "bg-white shadow-[0_6px_14px_rgba(124,58,237,0.35)]"
      : "bg-white shadow-sm";

    return (
      <div
        className={`w-full px-3 py-2 flex items-center justify-between gap-4 ${className}`}
      >
        <span className={`font-medium leading-5 ${labelColor}`}>
          Show Transcription
        </span>
        <button
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            handleToggleTranscription();
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7700FF]/40 ${switchBackground}`}
          role="switch"
          aria-checked={isActive}
          aria-label={
            isActive
              ? "Disable AI Captions"
              : "Enable AI Captions"
          }
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full transition-all duration-200 ease-out ${thumbTransform} ${thumbStyles}`}
          />
        </button>
      </div>
    );
  };

  const WaitingRoomToggleRow = ({ variant = "dark", className = "" }) => {
    const isLight = variant === "light";
    const labelColor = isLight ? "text-slate-800" : "text-white";
    const waitingRoomEnabled = meetingSettings?.hostApprovalRequired || meetingSettings?.waitingRoom;
    const switchBackground = waitingRoomEnabled
      ? "bg-[#7c3aed] shadow-[0_0_0_4px_rgba(124,58,237,0.25)]"
      : isLight
      ? "bg-slate-300"
      : "bg-gray-600";
    const thumbTransform = waitingRoomEnabled
      ? "translate-x-[1.4rem]"
      : "translate-x-1";
    const thumbStyles = waitingRoomEnabled
      ? "bg-white shadow-[0_6px_14px_rgba(124,58,237,0.35)]"
      : "bg-white shadow-sm";

    return (
      <div
        className={`w-full px-3 py-2 flex items-center justify-between gap-4 ${className}`}
      >
        <span className={` font-medium leading-5 ${labelColor}`}>
          Enable Waiting Room
        </span>
        <button
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            const newValue = !waitingRoomEnabled;
            // When disabling waiting room, admit all waiting participants BEFORE
            // state update (which disconnects the host waiting room socket)
            if (!newValue && admitAllWaitingParticipants) {
              admitAllWaitingParticipants();
            }
            setMeetingSettings((prev) => ({
              ...prev,
              hostApprovalRequired: newValue,
              waitingRoom: newValue,
            }));
            publishMeetingSettings({
              data: {
                ...meetingSettings,
                hostApprovalRequired: newValue,
                waitingRoom: newValue,
              },
            });
            // Only call API if authenticated (guest co-hosts rely on host to persist via PubSub relay)
            if (isAuthenticated) {
              meetingServices.updateMeetingSessionSettings({
                meetingId: meetingSettings?.roomId,
                sessionId: meetingSession,
                guestCanMute: meetingSettings?.guestCanUnmute,
                waitingRoomEnabled: newValue,
              });
            }
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7700FF]/40 ${switchBackground}`}
          role="switch"
          aria-checked={waitingRoomEnabled}
          aria-label={
            waitingRoomEnabled
              ? "Disable Waiting Room"
              : "Enable Waiting Room"
          }
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full transition-all duration-200 ease-out ${thumbTransform} ${thumbStyles}`}
          />
        </button>
      </div>
    );
  };

  const GuestCanUnmuteToggleRow = ({ variant = "dark", className = "" }) => {
    const isLight = variant === "light";
    const labelColor = isLight ? "text-slate-800" : "text-white";
    const switchBackground = meetingSettings?.guestCanUnmute
      ? "bg-[#7c3aed] shadow-[0_0_0_4px_rgba(124,58,237,0.25)]"
      : isLight
      ? "bg-slate-300"
      : "bg-gray-600";
    const thumbTransform = meetingSettings?.guestCanUnmute
      ? "translate-x-[1.4rem]"
      : "translate-x-1";
    const thumbStyles = meetingSettings?.guestCanUnmute
      ? "bg-white shadow-[0_6px_14px_rgba(124,58,237,0.35)]"
      : "bg-white shadow-sm";

    return (
      <div
        className={`w-full px-3 py-2 flex items-center justify-between gap-4 ${className}`}
      >
        <span className={`font-medium leading-5 ${labelColor}`}>
          {meetingSettings?.guestCanUnmute
            ? "Enable Guest Unmute"
            : "Enable Guest Unmute"}
        </span>
        <button
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            const newValue = !meetingSettings?.guestCanUnmute;
            setMeetingSettings((prev) => ({
              ...prev,
              guestCanUnmute: newValue,
            }));
            publishMeetingSettings({
              data: {
                ...meetingSettings,
                guestCanUnmute: newValue,
              },
            });

            // Only call API if authenticated (guest co-hosts rely on host to persist via PubSub relay)
            if (isAuthenticated) {
              meetingServices.updateMeetingSessionSettings({
                meetingId: meetingSettings?.roomId,
                sessionId: meetingSession,
                guestCanMute: newValue,
                waitingRoomEnabled: meetingSettings?.hostApprovalRequired || meetingSettings?.waitingRoom,
              });
            }
            localStorage.setItem(
              "guest-can-unmute-" + meetingSession,
              newValue
            );

            // Sync with Mute All: disabling guest unmute triggers mute all,
            // enabling guest unmute grants unmute all
            if (!newValue) {
              emitForceMuteAll();
            } else {
              emitGrantUnmuteAll();
            }
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7700FF]/40 ${switchBackground}`}
          role="switch"
          aria-checked={meetingSettings?.guestCanUnmute}
          aria-label={
            meetingSettings?.guestCanUnmute
              ? "Disable Guest Unmute"
              : "Enable Guest Unmute"
          }
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full transition-all duration-200 ease-out ${thumbTransform} ${thumbStyles}`}
          />
        </button>
      </div>
    );
  };

  const ApprovalSoundToggleRow = ({ variant = "dark", className = "" }) => {
    const isLight = variant === "light";
    const labelColor = isLight ? "text-slate-800" : "text-white";
    const switchBackground = approvalSoundEnabled
      ? "bg-[#7c3aed] shadow-[0_0_0_4px_rgba(124,58,237,0.25)]"
      : isLight
      ? "bg-slate-300"
      : "bg-gray-600";
    const thumbTransform = approvalSoundEnabled
      ? "translate-x-[1.4rem]"
      : "translate-x-1";
    const thumbStyles = approvalSoundEnabled
      ? "bg-white shadow-[0_6px_14px_rgba(124,58,237,0.35)]"
      : "bg-white shadow-sm";

    return (
      <div
        className={`w-full px-3 py-2 flex items-center justify-between gap-4 ${className}`}
      >
        <span className={`font-medium leading-5 ${labelColor}`}>
          Enable Approval Sound
        </span>
        <button
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
            setApprovalSoundEnabled((prev) => !prev);
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7700FF]/40 ${switchBackground}`}
          role="switch"
          aria-checked={approvalSoundEnabled}
          aria-label={
            approvalSoundEnabled
              ? "Disable Approval Sound"
              : "Enable Approval Sound"
          }
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full transition-all duration-200 ease-out ${thumbTransform} ${thumbStyles}`}
          />
        </button>
      </div>
    );
  };

  return (
    <div
      className="select-none sticky top-0 w-full z-40 border-b border-white/10 px-1 sm:px-2 md:px-3 lg:px-4 xl:px-6 bg-[#001233]/95 backdrop-blur-xl text-white flex items-center relative h-16 sm:h-18 md:h-[60px] transition-all duration-300 ease-in-out"
    >
      {/* Centered status pills (privacy / recording / notetaker / livestream) — stacked horizontally so they never overlap */}
      {(privacyMode || isRecording || isNotetakerActive || isLiveStreaming) && (
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none">
          {/* Privacy Mode pill — visible to ALL participants (host, co-host,
              presenter, attendee). Green locked padlock + tooltip on hover. */}
          {privacyMode && (
            <div
              className="pointer-events-auto relative"
              onMouseEnter={() => setPrivacyTooltipOpen(true)}
              onMouseLeave={() => setPrivacyTooltipOpen(false)}
              data-testid="privacy-mode-pill"
            >
              <button
                type="button"
                onClick={() => setPrivacyTooltipOpen((v) => !v)}
                className="flex items-center gap-2 bg-green-500/15 border border-green-500/40 text-white px-3 py-1.5 rounded-lg shadow-lg backdrop-blur-sm hover:bg-green-500/25 transition-colors"
                aria-label="Privacy Mode"
                title="Privacy Mode"
              >
                <Lock className="w-3.5 h-3.5 text-green-400" />
                <span className="text-[10px] tracking-widest font-semibold text-green-300">PRIVACY MODE ON</span>
              </button>
              {privacyTooltipOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 px-3 py-2 rounded-lg bg-[#0D1117] border border-white/15 text-xs text-gray-200 shadow-xl z-50"
                  role="tooltip"
                >
                  {PRIVACY_NOTICE_MESSAGE}
                </div>
              )}
            </div>
          )}
          {isRecording && (
            <div className="pointer-events-auto flex items-center gap-2 bg-[#001233]/80 border border-white/10 text-white px-3 py-1.5 rounded-lg shadow-lg backdrop-blur-sm">
              <span className={`inline-block w-2 h-2 rounded-xl ${isRecordingPaused ? 'bg-amber-400' : 'bg-red-500 animate-pulse'}`} />
              <span className="text-[10px] tracking-widest text-gray-300">{isRecordingPaused ? 'RECORDING PAUSED' : 'RECORDING'}</span>
              <span className="text-[10px] font-mono text-white">{formatDuration(displayedRecordingElapsedMs)}</span>
              {canPauseRecording && (
                <button
                  onClick={handleTogglePauseRecording}
                  className="ml-1 flex items-center justify-center w-4 h-4 text-gray-200 hover:text-white transition-colors"
                  title={isRecordingPaused ? 'Resume Recording' : 'Pause Recording'}
                  aria-label={isRecordingPaused ? 'Resume recording' : 'Pause recording'}
                >
                  {isRecordingPaused ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zm8 0h4v14h-4z" /></svg>
                  )}
                </button>
              )}
              {isHostOrCoHost && (
                <button
                  onClick={handleStopRecording}
                  className="ml-1 flex items-center justify-center w-3 h-3 text-gray-300 hover:text-white transition-colors bg-[#7700FF]"
                  title="Stop Recording"
                  aria-label="Stop recording"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#7700FF">
                    <path d="M6 6h12v12H6z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {isNotetakerActive && (
            <div className="pointer-events-auto flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf]/30 to-[#00c853]/30 border border-[#00c853]/40 text-white px-3 py-1.5 rounded-lg shadow-lg backdrop-blur-sm">
              <span className="inline-block w-2 h-2 bg-[#00c853] rounded-full animate-pulse" />
              <span className="text-[10px] tracking-widest text-gray-200 font-semibold">AI NOTETAKER</span>
              <span className="text-[10px] text-gray-300">Recording</span>
              {isHostOrCoHost && (
                <button
                  onClick={() => {
                    if (typeof props.onStopNotetaker === "function") props.onStopNotetaker();
                  }}
                  className="ml-1 flex items-center justify-center w-3 h-3 text-gray-300 hover:text-white transition-colors"
                  title="Stop AI Notetaker"
                  aria-label="Stop AI Notetaker"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 6h12v12H6z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {isLiveStreaming && (
            <div className="pointer-events-auto flex items-center gap-2 bg-[#001233]/80 border border-white/10 text-white px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm">
          <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-[10px] tracking-widest text-gray-300">
            LIVE
          </span>
          <div className="flex items-center gap-1">
            {liveStreamPlatforms.facebook && (
              <span className="text-[8px] bg-blue-600 px-1 py-0.5 rounded">
                FB
              </span>
            )}
            {liveStreamPlatforms.youtube && (
              <span className="text-[8px] bg-red-600 px-1 py-0.5 rounded">
                YT
              </span>
            )}
            {liveStreamPlatforms.linkedin && (
              <span className="text-[8px] bg-blue-700 px-1 py-0.5 rounded">
                LI
              </span>
            )}
          </div>
          <span className="text-[10px] font-mono text-white">
            {formatDuration(streamingElapsedMs)}
          </span>
          {isHostOrCoHost && (
            <button
              onClick={handleStopLiveStream}
              className="ml-2 flex items-center justify-center w-4 h-4 text-gray-300 hover:text-white transition-colors"
              title="Stop Live Stream"
              aria-label="Stop live stream"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 6h12v12H6z" />
              </svg>
            </button>
          )}
            </div>
          )}
        </div>
      )}
      {/* Left Section */}
      <div className="flex items-center justify-center w-3/7 h-full">
        {/* Desktop Logo */}
        <div className={`hidden lg:flex items-center mr-8 xl:mr-10 2xl:mr-12 transition-all duration-300 ease-in-out`}>
          <RLinkLogo size={compactMode ? "sm" : "md"} showText={!compactMode} companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
          {/* Breakout Room Name */}
          {currentBreakoutRoom?.roomName && (
            <div
              className="flex items-center gap-1.5 ml-4 px-3 py-1 rounded-lg bg-[#6a1fbf]/15 border border-[#6a1fbf]/30 text-sm font-medium text-[#c5a3ff] max-w-[240px]"
              title={currentBreakoutRoom.roomName}
            >
              <span className="text-[10px] tracking-widest font-semibold text-[#c5a3ff]/80">ROOM</span>
              <span className="truncate">{currentBreakoutRoom.roomName}</span>
            </div>
          )}
          {/* Breakout Room Timer */}
          {currentBreakoutRoom && timerRemaining != null && (
            <div className={`flex items-center gap-1.5 ml-2 px-3 py-1 rounded-lg border text-sm font-mono tabular-nums ${
              timerRemaining <= 60
                ? 'bg-red-500/15 border-red-500/30 text-red-400 animate-pulse'
                : timerRemaining <= 300
                  ? 'bg-[#ffa500]/15 border-[#ffa500]/30 text-[#ffa500]'
                  : 'bg-[#00c853]/10 border-[#00c853]/30 text-[#00c853]'
            }`}>
              <Clock className="w-3.5 h-3.5" />
              <span>{Math.floor(timerRemaining / 60)}:{(timerRemaining % 60).toString().padStart(2, '0')}</span>
            </div>
          )}
        </div>
        {/* Mobile Logo */}
        <div className="lg:hidden ml-2 sm:mr-8 flex items-center">
          <RLinkLogo size="sm" showText={false} companyLogo={wlBranding.companyLogo} companyName={wlBranding.companyName} />
          {/* Breakout Room Name (mobile) */}
          {currentBreakoutRoom?.roomName && (
            <div
              className="flex items-center ml-2 px-2 py-0.5 rounded-md bg-[#6a1fbf]/15 border border-[#6a1fbf]/30 text-xs font-medium text-[#c5a3ff] max-w-[120px]"
              title={currentBreakoutRoom.roomName}
            >
              <span className="truncate">{currentBreakoutRoom.roomName}</span>
            </div>
          )}
          {/* Breakout Room Timer (mobile) */}
          {currentBreakoutRoom && timerRemaining != null && (
            <div className={`flex items-center gap-1 ml-1.5 px-2 py-0.5 rounded-md border text-xs font-mono tabular-nums ${
              timerRemaining <= 60
                ? 'bg-red-500/15 border-red-500/30 text-red-400 animate-pulse'
                : timerRemaining <= 300
                  ? 'bg-[#ffa500]/15 border-[#ffa500]/30 text-[#ffa500]'
                  : 'bg-[#00c853]/10 border-[#00c853]/30 text-[#00c853]'
            }`}>
              <Clock className="w-3 h-3" />
              <span>{Math.floor(timerRemaining / 60)}:{(timerRemaining % 60).toString().padStart(2, '0')}</span>
            </div>
          )}
        </div>
        <div
          className={`${
            isMobileLike ? "hidden" : "flex"
          } items-center justify-center transition-all duration-300 ease-in-out h-full ${
            compactMode
              ? "gap-2 lg:gap-2.5 xl:gap-3 2xl:gap-4"
              : "gap-1.5 lg:gap-2 xl:gap-2.5 2xl:gap-3"
          }`}
        >
          {/* HIDDEN: Mic button - functionality moved to StudioBottomControls */}
          {false && (
          <div className="relative h-full">
            <button
              onClick={handleToggleMic}
              className="h-full flex items-center justify-center px-2 text-slate-300 hover:text-white "
              title={localMicOn ? "Mute" : "Unmute"}
              aria-label={localMicOn ? "Mute" : "Unmute"}
            >
              <div className="h-full flex items-center gap-1.5 transition-all duration-300 ease-in-out min-w-fit relative">
                <div className="flex flex-col items-center justify-center h-full ">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                    <img
                      src={
                        localMicState ? "/icons/micOn.svg" : "/icons/micOff.svg"
                      }
                      alt={localMicState ? "Mute" : "Unmute"}
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>

                  {!compactMode && (
                    <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                      Mic
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-center opacity-100 transition-all duration-300 ease-in-out">
                  <img
                    src="/icons/chevronDown.svg"
                    alt="dropdown"
                    className={`cursor-pointer transition-transform duration-200 ${
                      showMicDropdown ? "rotate-180" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMicDropdown(!showMicDropdown);
                      setShowVideoDropdown(false);
                      setShowSpeakerDropdown(false);
                    }}
                    title="Mic Settings"
                    draggable={false}
                  />
                </div>
              </div>
            </button>

            {/* Mic Device Dropdown */}
            {showMicDropdown && (
              <div
                data-mic-dropdown
                className={`absolute top-full left-0 ${
                  compactMode ? "-mt-4" : "mt-2"
                } w-64 bg-[#001233] rounded-lg shadow-xl border border-white/20 z-50 py-3 transition-all duration-300 ease-in-out`}
              >
                {/* Audio Input Section */}
                <div className="px-4 py-2">
                  <div className="text-white text-center text-sm tracking-widest font-medium mb-3">
                    AUDIO INPUT
                  </div>
                  <div className="relative">
                    <div
                      className="flex items-center justify-between w-full px-3 py-3 bg-white/5 border border-white/30 rounded-lg text-white text-sm"
                      onClick={() => {
                        setShowAudioInputDropdown(!showAudioInputDropdown);
                        setShowToolsDropdown(false);
                        setShowLayoutDropdown(false);
                        setShowVideoDropdown(false);
                        setShowMore(false);
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <img
                          src="/icons/micOn.svg"
                          alt="Microphone"
                          className="flex-shrink-0"
                        />
                        <span className="font-medium truncate min-w-0">
                          {selectedMic?.label || "Select microphone"}
                        </span>
                      </div>

                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                        className="flex-shrink-0 ml-2"
                      >
                        <path
                          d="M3.5 5.5L8 10l4.5-4.5"
                          stroke="currentColor"
                          stroke-width="1.4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          fill="none"
                        />
                      </svg>
                    </div>

                    {/* Device List */}
                    {showAudioInputDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-[#001233] border border-white/20 rounded-lg shadow-xl z-10">
                        {mics.map((device) => (
                          <button
                            key={device.deviceId}
                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-3 min-w-0"
                            onClick={() => {
                              changeMic(device.deviceId);
                              setShowMicDropdown(false);
                            }}
                          >
                            <img
                              src="/icons/micOn.svg"
                              alt="Microphone"
                              className="flex-shrink-0"
                            />
                            <span className="truncate min-w-0">
                              {device.label ||
                                `Microphone ${device.deviceId.slice(0, 8)}...`}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Audio Output Section */}
                <div className="px-4 py-2">
                  <div className="text-white text-center text-sm tracking-widest font-medium mb-3">
                    AUDIO OUTPUT
                  </div>
                  <div className="relative">
                    <button
                      onClick={() =>
                        setShowSpeakerDropdown(!showSpeakerDropdown)
                      }
                      className="flex items-center justify-between w-full px-3 py-3 bg-white/5 border border-white/30 rounded-lg text-white text-sm hover:bg-white/5/80 transition-colors min-w-0"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <svg
                          className="w-5 h-5 text-white flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                        </svg>
                        <span className="font-medium truncate min-w-0">
                          {selectedSpeaker?.label || "Select speaker"}
                        </span>
                      </div>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                        className={`flex-shrink-0 ml-2 transition-transform duration-200 ${
                          showSpeakerDropdown ? "rotate-180" : ""
                        }`}
                      >
                        <path
                          d="M3.5 5.5L8 10l4.5-4.5"
                          stroke="currentColor"
                          stroke-width="1.4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          fill="none"
                        />
                      </svg>
                    </button>

                    {/* Speaker List */}
                    {showSpeakerDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-2 max-h-48 overflow-y-auto bg-[#001233] border border-white/20 rounded-lg shadow-xl z-10 transition-all duration-300 ease-in-out">
                        {speakers.map((device) => (
                          <button
                            key={device.deviceId}
                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-3 min-w-0"
                            onClick={() => {
                              setSelectedSpeaker({
                                id: device.deviceId,
                                label: device.label,
                              });
                              setShowSpeakerDropdown(false);
                            }}
                          >
                            <svg
                              className="w-4 h-4 text-white flex-shrink-0"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                            </svg>
                            <span className="truncate min-w-0">
                              {device.label ||
                                `Speaker ${device.deviceId.slice(0, 8)}...`}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Refresh Devices Button */}
                <div className="px-4 py-2">
                  <button
                    onClick={() => {
                      getMics();
                      setShowMicDropdown(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-3 bg-[#7700FF] text-white text-sm rounded-lg hover:bg-[#7700FF]/80 transition-colors"
                  >
                    <img
                      src="/icons/refresh.png"
                      alt="Refresh Devices"
                      className=""
                    />
                    <span className="font-medium">REFRESH DEVICES</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* HIDDEN: Video button - functionality moved to StudioBottomControls */}
          {false && (
          <div className="relative">
            <button
              onClick={() => {
                setLocalWebcamState(!localWebcamState);

                if (localWebcamOn) {
                  mMeeting.disableWebcam();
                } else {
                  console.log(
                    "mMeeting.participants.size",
                    mMeeting.participants.size
                  );
                  if (
                    mMeeting.participants.size > 1 &&
                    videoResolution == "high"
                  ) {
                    handleVideoResolution(
                      mMeeting,
                      createCameraVideoTrack,
                      "low"
                    );
                  } else {
                    mMeeting.enableWebcam();
                  }
                }
              }}
              className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
              title={localWebcamOn ? "Camera Off" : "Camera On"}
              aria-label={localWebcamOn ? "Camera Off" : "Camera On"}
            >
              <div className="h-full flex items-center gap-1.5 transition-all duration-300 ease-in-out min-w-fit relative">
                {/* Icon and Label Container */}
                <div className="flex flex-col items-center justify-center">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                    <img
                      src={
                        localWebcamOn
                          ? "/icons/videoCamera.svg"
                          : "/icons/videoOff.svg"
                      }
                      alt={localWebcamState ? "Camera Off" : "Camera On"}
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                  {!compactMode && (
                    <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                      Video
                    </span>
                  )}
                </div>

                {/* Chevron - Right side, vertically centered */}
                <div className="flex items-center justify-center opacity-100 transition-all duration-300 ease-in-out">
                  <img
                    src="/icons/chevronDown.svg"
                    alt="dropdown"
                    className={`cursor-pointer transition-transform duration-200 ${
                      showVideoDropdown ? "rotate-180" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowVideoDropdown(!showVideoDropdown);
                      setShowMicDropdown(false);
                      setShowToolsDropdown(false);
                      setShowLayoutDropdown(false);
                      setShowMore(false);
                    }}
                    title="Video Settings"
                    draggable={false}
                  />
                </div>
              </div>
            </button>

            {/* Video Device Dropdown */}
            {showVideoDropdown && (
              <div
                data-video-dropdown
                className={`absolute top-full left-0 ${
                  compactMode ? "-mt-4" : "mt-2"
                } w-64 bg-[#001233] rounded-lg shadow-xl border border-white/20 z-50 py-3 transition-all duration-300 ease-in-out`}
              >
                {/* Video Source Section */}
                <div className="px-4 py-2">
                  <div className="text-white text-center text-sm tracking-widest font-medium mb-3">
                    VIDEO SOURCE
                  </div>
                  <div className="relative">
                    <div
                      className="flex items-center justify-between w-full px-3 py-3 bg-white/5 border border-white/30 rounded-lg text-white text-sm"
                      onClick={() => {
                        setShowVideoSourceDropdown(!showVideoSourceDropdown);
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <img
                          src="/icons/videoCamera.svg"
                          alt="Camera"
                          className="flex-shrink-0"
                        />
                        <span className="font-medium truncate min-w-0">
                          {selectedWebcam?.label || "Select camera"}
                        </span>
                      </div>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                        className="flex-shrink-0 ml-2"
                      >
                        <path
                          d="M3.5 5.5L8 10l4.5-4.5"
                          stroke="currentColor"
                          stroke-width="1.4"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          fill="none"
                        />
                      </svg>
                    </div>

                    {/* Camera List */}
                    {showVideoSourceDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-[#001233] border border-white/20 rounded-lg shadow-xl z-10">
                        {webcams.map((device) => (
                          <button
                            key={device.deviceId}
                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 transition-colors flex items-center gap-3 min-w-0"
                            onClick={() => {
                              changeWebcam(device.deviceId);
                              setShowVideoDropdown(false);
                            }}
                          >
                            <img
                              src="/icons/videoCamera.svg"
                              alt="Camera"
                              className="flex-shrink-0"
                            />
                            <span className="truncate min-w-0">
                              {device.label ||
                                `Camera ${device.deviceId.slice(0, 8)}...`}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Mirror Camera View Option */}
                <div className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-white text-sm">
                      Mirror Camera View
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="px-4 py-2 space-y-2">
                  <button
                    onClick={() => {
                      setShowVirtualBackgroundModal(true);
                      setShowVideoDropdown(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-3 bg-[#7700FF] text-white text-sm rounded-lg hover:bg-[#7700FF]/80 transition-colors"
                  >
                    <img
                      src="/icons/changeBg.png"
                      alt="Change Background"
                      className="w-4 h-4"
                    />
                    <span className="font-medium">CHANGE BACKGROUND</span>
                  </button>

                  <button
                    onClick={() => {
                      getWebcams();
                      setShowVideoDropdown(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-3 bg-[#7700FF] text-white text-sm rounded-lg hover:bg-[#7700FF]/80 transition-colors"
                  >
                    <img
                      src="/icons/refresh.png"
                      alt="Refresh Devices"
                      className=""
                    />
                    <span className="font-medium">REFRESH DEVICES</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* {!isMobile &&
            (["moderator", "co-host"].includes(meetingRole) ||
              (meetingSettings && meetingSettings.allowScreenSharing)) && (
              <div className="relative">
                <button
                  onClick={() => {
                    mMeeting.toggleScreenShare();
                  }}
                  className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                  title="Share"
                  aria-label="Share"
                >
                  <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                    <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                      <img
                        src="/icons/shareScreen.svg"
                        alt="Share"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {!compactMode && (
                      <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                        Share
                      </span>
                    )}
                  </div>
                </button>
              </div>
            )} */}
          {/* Raise Hand icon - HIDDEN for all users
          {meetingSettings && meetingSettings.allowRaiseHand && (
            <div className="relative">
              <button
                onClick={() => {
                  publishRaiseHand("Raise Hand");
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Raise"
                aria-label="Raise"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                    <img
                      src="/icons/raiseHand.svg"
                      alt="Raise"
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                  {!compactMode && (
                    <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                      Raise
                    </span>
                  )}
                </div>
              </button>
            </div>
          )}
          */}

          {/* Reactions icon - HIDDEN for all users
          {meetingSettings && meetingSettings.allowReactions && (
            <div className="relative">
              <button
                onClick={() => {
                  setShowReactions(!showReactions);
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Reactions"
                aria-label="Reactions"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                    <img
                      src="/icons/reaction.svg"
                      alt="Reactions"
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                  {!compactMode && (
                    <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                      Reactions
                    </span>
                  )}
                </div>
              </button>
              {showReactions && (
                <div
                  data-reactions-dropdown
                  className="absolute top-full right-0 md:right-auto md:left-0 mt-2 z-50 p-2 md:p-3 rounded-xl md:rounded-2xl shadow-2xl border border-gray-300 bg-white"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="grid grid-cols-5 gap-1 md:gap-2 min-w-[180px] md:min-w-[220px]">
                    {emojis.map(({ char, alt }) => (
                      <button
                        key={char}
                        aria-label={alt}
                        title={alt}
                        onClick={() => handleEmojiClick({ char, alt })}
                        className="w-8 h-8 md:w-10 md:h-10 text-lg md:text-2xl rounded-lg md:rounded-xl transition-all duration-200 ease-out bg-white border border-gray-200 hover:bg-gray-50 hover:scale-110 hover:-translate-y-0.5 hover:shadow-lg hover:border-gray-300 active:scale-95 flex items-center justify-center"
                      >
                        {char}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          */}

          {/* Share Screen button moved to right side */}
          {false && !isMobile &&
            (["moderator", "co-host"].includes(meetingRole) ||
              (meetingSettings && meetingSettings.allowScreenSharing)) && (
              <div className="relative">
                <button
                  onClick={() => {
                    mMeeting.toggleScreenShare();
                  }}
                  className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                  title="Share Screen"
                  aria-label="Share Screen"
                >
                  <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                    <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                      <img
                        src={
                          mMeeting.isScreenSharing
                            ? "/icons/shareScreennOn.svg"
                            : "/icons/shareScreenn.svg"
                        }
                        alt="Share Screen"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {!compactMode && (
                      <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                        Share Screen
                      </span>
                    )}
                  </div>
                </button>
              </div>
            )}

          {/* Record button moved to right side */}
          {false && ["moderator", "co-host"].includes(meetingRole) && (
            <div className="relative">
              <button
                onClick={() => {
                  handleRecordingClick();
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Record"
                aria-label="Record"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                    <img
                      src={
                        mMeeting.recordingState ==
                        Constants.recordingEvents.RECORDING_STOPPED
                          ? "/icons/recordOff.svg"
                          : "/icons/recordOn.svg"
                      }
                      alt="Record"
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                  {!compactMode && (
                    <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                      Record
                    </span>
                  )}
                </div>
              </button>
            </div>
          )}

          {meetingSettings &&
            meetingSettings.customButtonEnabled !== false &&
            meetingSettings.customButtonText &&
            meetingSettings.customButtonUrl && (
              <button
                onClick={() => {
                  window.open(meetingSettings.customButtonUrl, "_blank");
                }}
                className={`p-0 hover:text-white bg-[#7700FF] text-white rounded-md ${
                  compactMode
                    ? "text-xs px-1 py-1 transition-all duration-300 ease-in-out"
                    : "text-sm mx-0.5 lg:mx-1 sm:text-sm px-1 py-1 sm:py-2 transition-all duration-300 ease-in-out"
                }`}
                title={meetingSettings.customButtonText}
                aria-label={meetingSettings.customButtonText}
              >
                {meetingSettings.customButtonText}
              </button>
            )}
        </div>
      </div>

      {/* Center Section - Meeting Name */}
      <div className="flex-1 flex justify-center items-center w-1/7">
        <div className="hidden bg-gray-800 px-1 sm:px-2 md:px-3 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 max-w-full">
          {/* {showMeetingName && (
            <span className="text-[#7700FF] font-medium text-xs sm:text-sm md:text-base tracking-wide truncate">
              {meetingSettings?.roomName || "Meeting Name"}
            </span>
          )} */}
          {/* Chevron moved into bottom notch */}
          <span className="hidden" />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center flex-shrink-0 w-3/7">
        {/* Mobile Right Section - Participant and Profile */}
        <div
          className={`${isMobileLike ? "flex" : "hidden"} items-center gap-4`}
        >
          {/* Mobile Participant Toggle - Hidden */}
          {false && (
            <button
              onClick={() => {
                setSideBarMode((s) =>
                  s === sideBarModes.PARTICIPANTS
                    ? null
                    : sideBarModes.PARTICIPANTS
                );
              }}
              className="relative flex items-center gap-1 text-white focus:outline-none"
              title="Participants"
              aria-label="Participants"
              type="button"
            >
              <span className="text-sm font-medium">
                {mMeeting?.participants?.size || 0}
              </span>
              <div className="relative">
                {participantApproveFunc?.length > 0 && ["moderator", "co-host", "host"].includes(meetingRole) && (
                  <span className="absolute -top-1 -right-1 z-10 inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full bg-red-500 text-white text-[9px] font-semibold animate-pulse">
                    {participantApproveFunc.length}
                  </span>
                )}
                <img
                  src="/icons/participants.svg"
                  alt="Participants"
                  className="w-5"
                />
              </div>
            </button>
          )}

          {/* Share Screen Button - studiodev style */}
          {!isMobile && (() => {
            // Check if user is allowed to screen share:
            // 1. Host/co-host/moderator are always allowed
            // 2. Attendees who have been made presenter are allowed
            const isHostOrCoHost = ["moderator", "co-host", "host", "speaker"].includes(meetingRole);
            const localParticipantId = mMeeting?.localParticipant?.id;
            const numericLocalId = stringToNumericUid(localParticipantId);
            const isPresenter = localParticipantId && (
              presenters?.has(localParticipantId) ||
              presenters?.has(String(localParticipantId)) ||
              presenters?.has(numericLocalId)
            );
            const canScreenShare = isHostOrCoHost || isPresenter;

            // The "Allow Screen Sharing" room setting only restricts attendees —
            // hosts/co-hosts/speakers/presenters can always share regardless.
            if (meetingSettings?.allowScreenSharing === false && !canScreenShare) return null;

            // If someone else is already presenting, disable the button
            const isDisabledByPresenter = mMeeting?.presenterId && !mMeeting.isScreenSharing;
            const isDisabled = !canScreenShare || isDisabledByPresenter;

            const getTooltip = () => {
              if (!canScreenShare) return "Only hosts or presenters can share screen";
              if (mMeeting.isScreenSharing) return "Stop Presenting";
              if (isDisabledByPresenter) return "Someone else is presenting";
              return "Share Screen";
            };

            return (
              <button
                onClick={() => {
                  if (!isDisabled) {
                    mMeeting.toggleScreenShare();
                  }
                }}
                className={`hidden sm:flex flex-col items-center justify-center px-3 py-1.5 rounded-lg transition-colors min-w-[56px] ${
                  isDisabled
                    ? 'text-gray-500 cursor-not-allowed opacity-60'
                    : mMeeting.isScreenSharing
                      ? 'bg-[#6a1fbf]/20 text-[#6a1fbf]'
                      : 'text-[#c084fc] hover:text-[#a64dff] hover:bg-white/10'
                }`}
                title={getTooltip()}
                aria-label={getTooltip()}
                disabled={isDisabled}
              >
                {mMeeting.isScreenSharing ? (
                  <MonitorOff className="w-5 h-5 mb-0.5 flex-shrink-0" />
                ) : (
                  <Monitor className="w-5 h-5 mb-0.5 flex-shrink-0" />
                )}
                <span className="text-[10px]">{mMeeting.isScreenSharing ? 'Stop' : 'Share'}</span>
              </button>
            );
          })()}

          {/* Record Button - studiodev style */}
          {["moderator", "co-host"].includes(meetingRole) && (
            <button
              onClick={() => {
                handleRecordingClick();
              }}
              className={`flex flex-col items-center justify-center px-3 py-1.5 rounded-lg transition-colors min-w-[56px] ${
                mMeeting.recordingState !== Constants.recordingEvents.RECORDING_STOPPED
                  ? 'bg-red-500/20 text-red-400'
                  : 'text-red-300 hover:text-red-200 hover:bg-white/10'
              }`}
              title="Record"
              aria-label="Record"
            >
              <div className={`w-4 h-4 rounded-full mb-0.5 flex-shrink-0 ${
                mMeeting.recordingState !== Constants.recordingEvents.RECORDING_STOPPED
                  ? 'bg-red-500 animate-pulse'
                  : 'border-2'
              }`} style={mMeeting.recordingState === Constants.recordingEvents.RECORDING_STOPPED ? { borderColor: 'currentColor' } : {}} />
              <span className="text-[10px]">
                {mMeeting.recordingState !== Constants.recordingEvents.RECORDING_STOPPED ? 'Stop' : 'Record'}
              </span>
            </button>
          )}

          {/* Custom Button (mobile) */}
          {meetingSettings &&
            meetingSettings.customButtonEnabled !== false &&
            meetingSettings.customButtonText &&
            meetingSettings.customButtonUrl && (
              <button
                onClick={() => {
                  window.open(meetingSettings.customButtonUrl, "_blank");
                }}
                className="text-xs px-2 py-1 bg-[#7700FF] text-white rounded-md hover:text-white transition-all duration-300 ease-in-out truncate max-w-[100px]"
                title={meetingSettings.customButtonText}
                aria-label={meetingSettings.customButtonText}
              >
                {meetingSettings.customButtonText}
              </button>
            )}

          {/* Profile Menu */}
          <div className="relative">
            <button
              data-profile-button
              onClick={() => {
                setShowProfileDropdown(!showProfileDropdown);
              }}
              className={`flex items-center gap-1 md:gap-2 p-1 md:p-2 hover:bg-white/10 rounded-lg transition-colors ${
                isMobileLike ? "mr-2" : ""
              }`}
              title="Profile"
              aria-label="Profile"
            >
              {user?.logo ? (
                <img
                  alt="Profile"
                  className="w-6 h-6 md:w-7 md:h-7 rounded-full object-cover border border-white/20 flex-shrink-0"
                  src={user?.logo}
                />
              ) : (
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] md:text-xs font-bold text-white">
                    {user?.name?.[0]?.toUpperCase() || localStorage.getItem("participantName")?.[0]?.toUpperCase() || "G"}
                  </span>
                </div>
              )}
              <ChevronDown className={`w-3.5 h-3.5 text-gray-400 hidden sm:block flex-shrink-0 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && (
              <div
                data-profile-dropdown
                className="absolute top-full right-0 mt-2 w-48 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl z-[100]"
              >
                <div className="px-3 py-2 border-b border-white/10">
                  <p className="font-medium text-white truncate">{user?.name || localStorage.getItem("participantName") || "Guest"}</p>
                  <p className="text-sm text-gray-500">{meetingRole === "moderator" ? "Host" : meetingRole === "co-host" ? "Co-Host" : "Participant"}</p>
                </div>
                <div className="py-1">
                  {isAuthenticated && (
                    <>
                      <a
                        href="/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </a>
                      <a
                        href="/dashboard/profile"
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseDown={(e) => e.stopPropagation()}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        Profile & Settings
                      </a>
                    </>
                  )}
                </div>
                <div className="border-t border-white/10 py-1">
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => {
                      setShowProfileDropdown(false);
                      mMeeting?.leave();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-white/5 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Leave Meeting
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Hamburger Icon - landscape mobile only.
              Reverted from showing in portrait too because the panel it opens
              contains the legacy SettingsPanel which isn't responsive yet.
              Re-enable for portrait once mobile responsiveness lands. */}
          {isMobile && isMobileLandscape && (
            <button
              onClick={() => {
                if (isAuthenticated) {
                  setShowMobilePanel(!showMobilePanel);
                }
              }}
              className={`p-0 text-slate-300 hover:text-white transition-colors duration-200 ${
                isMobileLike ? "mr-2" : ""
              }`}
              title="Hamburger"
              aria-label="Hamburger"
            >
              <img
                src="/icons/hamburger.svg"
                alt="Hamburger"
                className=" object-cover  hover:border-gray-400 transition-all duration-200"
              />
            </button>
          )}
        </div>

        {/* Start / End Webinar — primary host action, visible at every
            non-mobile breakpoint. Kept outside the breakpoint-gated blocks
            below so narrow desktop windows (below sm) still surface it. */}
        {!isMobileLike && (showStartWebinarButton || showEndWebinarButton) && (
          <div className="flex items-center gap-1.5 mr-1.5">
            {showStartWebinarButton && (
              <button
                onClick={handleStartWebinar}
                disabled={startingWebinar}
                className="flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 lg:px-4 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs md:text-sm font-semibold shadow-lg shadow-[#00c853]/20 transition-all"
                title="Start the webinar"
                aria-label="Start Webinar"
              >
                {startingWebinar ? (
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" /></svg>
                ) : (
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                )}
                <span>{startingWebinar ? 'Starting…' : (<><span className="lg:hidden">Start</span><span className="hidden lg:inline">Start Webinar</span></>)}</span>
              </button>
            )}
            {showEndWebinarButton && (
              <button
                onClick={handleEndWebinar}
                disabled={endingWebinar}
                className="flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 lg:px-4 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs md:text-sm font-semibold shadow-lg transition-all"
                title="End Webinar"
                aria-label="End Webinar"
              >
                {endingWebinar ? (
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56" strokeLinecap="round" /></svg>
                ) : (
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" /></svg>
                )}
                <span>{endingWebinar ? 'Ending…' : (<><span className="lg:hidden">End</span><span className="hidden lg:inline">End Webinar</span></>)}</span>
              </button>
            )}
          </div>
        )}

        {!isMobileLike && (
          <div
            className={`hidden lg:flex items-center transition-all duration-300 ease-in-out ${
              compactMode
                ? "gap-2 xl:gap-2.5 2xl:gap-3"
                : "gap-1.5 xl:gap-2 2xl:gap-2.5"
            }`}
          >
            {/* Elements button - Hidden */}
            {false && ["moderator", "co-host"].includes(meetingRole) && (
              <div className="relative">
                <button
                  onClick={() => {
                    setShowElementSection(!showElementSection);
                  }}
                  className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                  title="Elements"
                  aria-label="Elements"
                >
                  <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                    <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                      <img
                        src="/icons/elements.svg"
                        alt="Elements"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {!compactMode && (
                      <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                        Elements
                      </span>
                    )}
                  </div>
                </button>
              </div>
            )}
            {/* Chat icon - HIDDEN for all users
            {meetingSettings && meetingSettings.allowChat && (
              <div className="relative">
                <button
                  onClick={() => {
                    setSideBarMode((s) =>
                      s === sideBarModes.CHAT ? null : sideBarModes.CHAT
                    );
                    setUnreadChat(false);
                  }}
                  className="flex flex-col items-center justify-center px-3 py-1.5 rounded-lg transition-colors min-w-[56px] text-gray-400 hover:text-white hover:bg-white/10"
                  title="Chat"
                  aria-label="Chat"
                >
                  <img
                    src="/icons/chat.svg"
                    alt="Chat"
                    className="w-4 h-4 mb-0.5 flex-shrink-0 object-contain"
                    draggable={false}
                  />
                  <span className="text-[10px]">Chat</span>
                </button>
                {unreadChat && (
                  <span className="absolute top-1 right-2 w-2 h-2 rounded-full bg-red-500"></span>
                )}
              </div>
            )}
            */}

            {/* Rewards Button with Dropdown */}
            <div className="relative menu-container">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRewardsPanel(!showRewardsPanel);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  showRewardsPanel
                    ? 'bg-[#8b5cf6]/15 border-[#8b5cf6]/40'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                }`}
                title="Rewards"
                aria-label="Rewards"
              >
                <Coins className="w-5 h-5 text-[#8b5cf6] flex-shrink-0" />
                <div className="flex flex-col items-start">
                  <span className="text-xs text-gray-400 leading-tight">Rewards</span>
                  <span className="text-sm font-semibold text-white leading-tight">{rewardBalance.toFixed(2)}</span>
                </div>
              </button>
              {showRewardsPanel && (
                <div className="absolute top-full right-0 mt-2 w-[445px] bg-[#0a0a1a] border border-white/10 rounded-xl shadow-2xl z-[9999] overflow-hidden" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                  <RewardsDropdownPanel balance={null} onClose={() => setShowRewardsPanel(false)} />
                </div>
              )}
            </div>

            {/* Share Screen Button - studiodev style (Desktop) */}
            {(() => {
              // Check if user is allowed to screen share:
              // 1. Host/co-host/moderator are always allowed
              // 2. Attendees who have been made presenter are allowed
              const isHostOrCoHost = ["moderator", "co-host", "host", "speaker"].includes(meetingRole);
              const localParticipantId = mMeeting?.localParticipant?.id;
              const numericLocalId = stringToNumericUid(localParticipantId);
              const isPresenter = localParticipantId && (
                presenters?.has(localParticipantId) ||
                presenters?.has(String(localParticipantId)) ||
                presenters?.has(numericLocalId)
              );
              const canScreenShare = isHostOrCoHost || isPresenter;

              // The "Allow Screen Sharing" room setting only restricts attendees —
              // hosts/co-hosts/speakers/presenters can always share regardless.
              if (meetingSettings?.allowScreenSharing === false && !canScreenShare) return null;

              // If someone else is already presenting, disable the button
              const isDisabledByPresenter = mMeeting?.presenterId && !mMeeting.isScreenSharing;
              const isDisabled = !canScreenShare || isDisabledByPresenter;

              const getTooltip = () => {
                if (!canScreenShare) return "Only hosts or presenters can share screen";
                if (mMeeting.isScreenSharing) return "Stop Presenting";
                if (isDisabledByPresenter) return "Someone else is presenting";
                return "Share Screen";
              };

              return (
                <button
                  onClick={() => {
                    if (!isDisabled) {
                      mMeeting.toggleScreenShare();
                    }
                  }}
                  className={`hidden sm:flex flex-col items-center justify-center px-3 py-1.5 rounded-lg transition-colors min-w-[56px] ${
                    isDisabled
                      ? 'text-gray-500 cursor-not-allowed opacity-60'
                      : mMeeting.isScreenSharing
                        ? 'bg-[#6a1fbf]/20 text-[#6a1fbf]'
                        : 'text-[#c084fc] hover:text-[#a64dff] hover:bg-white/10'
                  }`}
                  title={getTooltip()}
                  aria-label={getTooltip()}
                  disabled={isDisabled}
                >
                  {mMeeting.isScreenSharing ? (
                    <MonitorOff className="w-5 h-5 mb-0.5 flex-shrink-0" />
                  ) : (
                    <Monitor className="w-5 h-5 mb-0.5 flex-shrink-0" />
                  )}
                  <span className="text-[10px]">{mMeeting.isScreenSharing ? 'Stop' : 'Share'}</span>
                </button>
              );
            })()}

            {/* Record Button - studiodev style (Desktop) */}
            {["moderator", "co-host"].includes(meetingRole) && (
              <button
                onClick={() => {
                  handleRecordingClick();
                }}
                className={`flex flex-col items-center justify-center px-3 py-1.5 rounded-lg transition-colors min-w-[56px] ${
                  mMeeting.recordingState !== Constants.recordingEvents.RECORDING_STOPPED
                    ? 'bg-red-500/20 text-red-400'
                    : 'text-red-300 hover:text-red-200 hover:bg-white/10'
                }`}
                title="Record"
                aria-label="Record"
              >
                <div className={`w-4 h-4 rounded-full mb-0.5 flex-shrink-0 ${
                  mMeeting.recordingState !== Constants.recordingEvents.RECORDING_STOPPED
                    ? 'bg-red-500 animate-pulse'
                    : 'border-2'
                }`} style={mMeeting.recordingState === Constants.recordingEvents.RECORDING_STOPPED ? { borderColor: 'currentColor' } : {}} />
                <span className="text-[10px]">
                  {mMeeting.recordingState !== Constants.recordingEvents.RECORDING_STOPPED ? 'Stop' : 'Record'}
                </span>
              </button>
            )}

            {/* Pause/Resume Recording — host/co-host, web mode only */}
            {canPauseRecording && (
              <button
                onClick={handleTogglePauseRecording}
                className={`flex flex-col items-center justify-center px-3 py-1.5 rounded-lg transition-colors min-w-[56px] ${
                  isRecordingPaused
                    ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                    : 'text-amber-300 hover:text-amber-200 hover:bg-white/10'
                }`}
                title={isRecordingPaused ? 'Resume Recording' : 'Pause Recording'}
                aria-label={isRecordingPaused ? 'Resume Recording' : 'Pause Recording'}
              >
                {isRecordingPaused ? (
                  <svg className="w-4 h-4 mb-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                ) : (
                  <svg className="w-4 h-4 mb-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zm8 0h4v14h-4z" /></svg>
                )}
                <span className="text-[10px]">{isRecordingPaused ? 'Resume' : 'Pause'}</span>
              </button>
            )}

            {/* AI Notes Button + Dropdown — host/co-host only */}
            {isHostOrCoHost && (
              <div className="relative">
                <button
                  ref={notetakerBtnRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotetakerDropdown(!showNotetakerDropdown);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                    isNotetakerActive
                      ? "bg-gradient-to-r from-[#6a1fbf]/20 to-[#00c853]/20 border-[#00c853]/40"
                      : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                  }`}
                  title={isNotetakerActive ? "AI Notetaker is recording" : "AI Notes"}
                  aria-label="AI Notes"
                >
                  <Sparkles className={`w-4 h-4 ${isNotetakerActive ? "text-[#00c853]" : "text-[#a78bfa]"}`} />
                  <span className="text-sm font-medium text-white hidden md:inline">AI Notes</span>
                  {isNotetakerActive && (
                    <span className="inline-block w-1.5 h-1.5 bg-[#00c853] rounded-full animate-pulse" />
                  )}
                </button>

                {showNotetakerDropdown && (
                  <div
                    ref={notetakerDropdownRef}
                    className="absolute top-full right-0 mt-2 w-80 bg-[#0a0a1a]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl z-[9999] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">AI Notetaker</p>
                          <p className="text-[10px] text-gray-400">Powered by OpenAI</p>
                        </div>
                      </div>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          console.log('[AI Notetaker] toggle clicked, active=', isNotetakerActive, 'hasStart=', typeof props.onStartNotetaker, 'hasStop=', typeof props.onStopNotetaker);
                          if (isNotetakerActive) {
                            if (typeof props.onStopNotetaker === "function") {
                              await props.onStopNotetaker();
                            } else {
                              console.warn('[AI Notetaker] onStopNotetaker prop is missing');
                            }
                          } else {
                            if (typeof props.onStartNotetaker === "function") {
                              await props.onStartNotetaker();
                            } else {
                              console.warn('[AI Notetaker] onStartNotetaker prop is missing');
                            }
                          }
                        }}
                        className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                          isNotetakerActive ? "bg-gradient-to-r from-[#6a1fbf] to-[#00c853]" : "bg-gray-600"
                        }`}
                        role="switch"
                        aria-checked={isNotetakerActive}
                      >
                        <div
                          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            isNotetakerActive ? "translate-x-[22px]" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    {/* Body */}
                    <div className="p-4 space-y-3">
                      {/* Status */}
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg">
                        {isNotetakerActive ? (
                          <>
                            <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                            <Mic className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                            <span className="text-xs text-white">Recording &amp; transcribing...</span>
                          </>
                        ) : (
                          <>
                            <Mic className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-400">Not recording</span>
                          </>
                        )}
                      </div>

                      {/* Meeting Type */}
                      <div>
                        <label className="text-[10px] text-gray-400 block mb-1.5">Meeting Type</label>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMeetingTypeOptions(!showMeetingTypeOptions);
                            }}
                            className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/15 rounded-lg text-white text-xs hover:bg-white/10 transition-all"
                          >
                            <span>{meetingTypeOptions.find((o) => o.id === notetakerMeetingType)?.label || "Auto-detect"}</span>
                            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showMeetingTypeOptions ? "rotate-180" : ""}`} />
                          </button>
                          {showMeetingTypeOptions && (
                            <div className="absolute left-0 right-0 top-full mt-1 bg-[#0a0a1a] border border-white/15 rounded-lg shadow-xl z-[10000] max-h-56 overflow-y-auto">
                              {meetingTypeOptions.map((opt) => (
                                <button
                                  key={opt.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setNotetakerMeetingType(opt.id);
                                    setShowMeetingTypeOptions(false);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                    notetakerMeetingType === opt.id ? "bg-[#6a1fbf]/20 text-white" : "text-gray-300 hover:bg-white/10"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Show bot in participants */}
                      <div className="flex items-center justify-between px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg">
                        <span className="text-xs text-white">Show bot in participants</span>
                        <button
                          onClick={() => {
                            const next = !showBotInParticipants;
                            setShowBotInParticipants(next);
                            if (typeof window !== "undefined") {
                              window.__rlinkShowSttBot = next;
                              // Notify AgoraContext + any other listeners that filter rules changed
                              window.dispatchEvent(new CustomEvent('rlink-show-stt-bot-changed', { detail: { show: next } }));
                            }
                          }}
                          className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                            showBotInParticipants ? "bg-gradient-to-r from-[#6a1fbf] to-[#00c853]" : "bg-gray-600"
                          }`}
                          role="switch"
                          aria-checked={showBotInParticipants}
                        >
                          <div
                            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              showBotInParticipants ? "translate-x-[18px]" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </div>

                      {/* AI will generate */}
                      <div>
                        <p className="text-[10px] text-gray-400 mb-2">AI will generate:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {["Summary", "Action Items", "Decisions", "Questions"].map((chip) => (
                            <span
                              key={chip}
                              className="px-2 py-1 bg-[#6a1fbf]/20 border border-[#6a1fbf]/30 text-[10px] text-white rounded-full"
                            >
                              {chip}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Layout button - Hidden */}
            {false && (
              <div className="relative">
                <button
                  onClick={() => {
                    setShowLayoutDropdown(!showLayoutDropdown);
                    setShowMicDropdown(false);
                    setShowVideoDropdown(false);
                    setShowSpeakerDropdown(false);
                    setShowCaptionsDropdown(false);
                    setShowToolsDropdown(false);
                    setShowMore(false);
                  }}
                  className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                  title="Layout"
                  aria-label="Layout"
                >
                  <div className="h-full flex items-center gap-1.5 transition-all duration-300 ease-in-out min-w-fit relative">
                    {/* Icon and Label Container */}
                    <div className="flex flex-col items-center justify-center">
                      <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-5 lg:h-5">
                        <img
                          src="/icons/layout.svg"
                          alt="Layout"
                          className="object-contain hover:opacity-80 transition-all duration-200"
                          draggable={false}
                        />
                      </div>
                      {!compactMode && (
                        <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                          Layout
                        </span>
                      )}
                    </div>

                    {/* Chevron - Right side, vertically centered */}
                    <div className="flex items-center justify-center opacity-100 transition-all duration-300 ease-in-out">
                      <img
                        src="/icons/chevronDown.svg"
                        alt="dropdown"
                        className={`cursor-pointer transition-transform duration-200 ${
                          showLayoutDropdown ? "rotate-180" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowLayoutDropdown(!showLayoutDropdown);
                          setShowMicDropdown(false);
                          setShowVideoDropdown(false);
                          setShowSpeakerDropdown(false);
                          setShowCaptionsDropdown(false);
                          setShowToolsDropdown(false);
                          setShowMore(false);
                        }}
                        title="Layout Settings"
                        draggable={false}
                      />
                    </div>
                  </div>
                </button>

                {/* Layout Dropdown */}
                {showLayoutDropdown && (
                <div
                  data-layout-dropdown
                  className={`absolute top-full right-0 ${
                    compactMode ? "-mt-4" : "mt-2"
                  } w-80 bg-[#001233] rounded-lg shadow-xl border border-white/20 z-50 py-3 px-4 transition-all duration-300 ease-in-out`}
                >
                  {/* VIEW Section */}
                  <div className="px-4 py-2">
                    <div className="text-white text-center text-sm tracking-widest font-medium mb-5">
                      VIEW
                    </div>

                    {/* Layout Options */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Gallery Layout */}
                      <button
                        onClick={() => {
                          setSelectedLayout("gallery");
                          setShowLayoutDropdown(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "gallery"
                            ? "border-[#7700FF] bg-[#7700FF]/10"
                            : "border-white/20 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/gallery.svg"
                              alt="gallery"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">Gallery</span>
                        </div>
                      </button>

                      {/* Speaker Top Layout */}
                      <button
                        onClick={() => {
                          setSelectedLayout("speaker-top");
                          setShowLayoutDropdown(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "speaker-top"
                            ? "border-[#7700FF] bg-[#7700FF]/10"
                            : "border-white/20 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/speakerTop.svg"
                              alt="speakerTop"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">
                            Speaker Top
                          </span>
                        </div>
                      </button>

                      {/* Focus Layout */}
                      <button
                        onClick={() => {
                          setSelectedLayout("focus");
                          setShowLayoutDropdown(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "focus"
                            ? "border-[#7700FF] bg-[#7700FF]/10"
                            : "border-white/20 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/speakerFocus.svg"
                              alt="focus"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">Focus</span>
                        </div>
                      </button>

                      {/* Speaker Side Layout */}
                      <button
                        onClick={() => {
                          setSelectedLayout("speaker-side");
                          setShowLayoutDropdown(false);
                        }}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedLayout === "speaker-side"
                            ? "border-[#7700FF] bg-[#7700FF]/10"
                            : "border-white/20 hover:border-gray-500"
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="">
                            <img
                              src="/icons/speakerSide.svg"
                              alt="speakerSide"
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <span className="text-white text-xs">
                            Speaker Side
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Separator */}
                  <div className="border-t border-white/20 my-4"></div>

                  {/* OPTIONS Section */}
                  <div className="px-4 py-2">
                    <div className="text-white text-center text-sm tracking-widest font-medium mb-3">
                      OPTIONS
                    </div>

                    <div className="space-y-2">
                      {/* Fullscreen Option */}
                      <button
                        onClick={() => {
                          setIsFullscreen(!isFullscreen);
                          setShowLayoutDropdown(false);
                          handleToggleFullscreen();
                        }}
                        className={`w-full text-left px-3 py-2 text-sm ${
                          !isFullscreen
                            ? "text-gray-400 hover:text-white"
                            : "text-white hover:bg-white/10 transition-colors"
                        }`}
                      >
                        Fullscreen
                      </button>

                      {/* Hide self view Option */}
                      {mMeeting &&
                        mMeeting.participants &&
                        Array.from(mMeeting.participants.keys()).length > 1 && (
                          <button
                            onClick={() => {
                              setHideSelfView(!hideSelfView);
                              setShowLayoutDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm ${
                              hideSelfView ? "text-white" : "text-gray-400"
                            }`}
                          >
                            Hide self view
                          </button>
                        )}

                      {/* Show non-video participants Option */}
                      {selectedLayout == "gallery" && (
                        <button
                          onClick={() => {
                            setShowNonVideoParticipants(
                              !showNonVideoParticipants
                            );
                            setShowLayoutDropdown(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            showNonVideoParticipants
                              ? "text-white"
                              : "text-gray-400"
                          }`}
                        >
                          Show non-video participants
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                )}
              </div>
            )}

            {/* Settings Button - Hidden (moved to bottom bar) */}
            {false && <button
              onClick={() => {
                setShowMore(!showMore);
                setShowLayoutDropdown(false);
                setShowMicDropdown(false);
                setShowVideoDropdown(false);
                setShowSpeakerDropdown(false);
                setShowCaptionsDropdown(false);
                setShowToolsDropdown(false);
              }}
              className={`flex items-center justify-center p-2 rounded-lg transition-colors ${
                showMore
                  ? 'bg-white/10 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              title="Settings"
              aria-label="Settings"
            >
              <img
                src="/icons/settings.svg"
                alt="Settings"
                className="w-5 h-5 object-contain"
                draggable={false}
              />
            </button>}

            {/* Profile Menu - studiodev style */}
            <div className="relative">
              <button
                data-profile-button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="flex items-center gap-1 md:gap-2 p-1 md:p-2 hover:bg-white/10 rounded-lg transition-colors"
                title="Profile"
                aria-label="Profile"
              >
                {user?.logo ? (
                  <img
                    src={user.logo}
                    alt="Profile"
                    className="object-cover rounded-full w-6 h-6 md:w-7 md:h-7 flex-shrink-0"
                    draggable={false}
                  />
                ) : (
                  <div
                    className="w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${branding?.primaryColor || '#6a1fbf'}, ${branding?.accentColor || '#00c853'})` }}
                  >
                    <span className="text-[10px] md:text-xs font-bold text-white">
                      {user?.name?.[0]?.toUpperCase() || localStorage.getItem("participantName")?.[0]?.toUpperCase() || "G"}
                    </span>
                  </div>
                )}
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden sm:block flex-shrink-0" />
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <div
                  data-profile-dropdown
                  className="absolute top-full right-0 mt-2 w-48 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl z-[100]"
                >
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="font-medium text-white truncate">{user?.name || localStorage.getItem("participantName") || "Guest"}</p>
                    <p className="text-sm text-gray-500">{meetingRole === "moderator" ? "Host" : meetingRole === "co-host" ? "Co-Host" : "Participant"}</p>
                  </div>
                  <div className="py-1">
                    {isAuthenticated && (
                      <>
                        <a
                          href="/dashboard"
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </a>
                        <a
                          href="/dashboard/profile"
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          Profile & Settings
                        </a>
                      </>
                    )}
                  </div>
                  <div className="border-t border-white/10 py-1">
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        setShowProfileDropdown(false);
                        mMeeting?.leave();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-white/5 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Leave Meeting
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Small Desktop Screen - Show minimal icons */}
        {!isMobileLike && (
          <div className="hidden sm:flex md:hidden items-center gap-1.5">
            {/* Chat icon - HIDDEN for all users (Small Desktop)
            {meetingSettings && meetingSettings.allowChat && (
              <div className="relative">
                <button
                  onClick={() => {
                    setSideBarMode((s) =>
                      s === sideBarModes.CHAT ? null : sideBarModes.CHAT
                    );
                    setUnreadChat(false);
                  }}
                  className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                  title="Chat"
                  aria-label="Chat"
                >
                  <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit h-6 sm:h-8">
                    <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                      <img
                        src="/icons/chat.svg"
                        alt="Chat"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                  </div>
                </button>
                {unreadChat && (
                  <div className="pointer-events-none absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full z-20"></div>
                )}
              </div>
            )}
            */}

            {/* Rewards Button - Small Desktop with Dropdown */}
            <div className="relative menu-container">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRewardsPanel(!showRewardsPanel);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  showRewardsPanel
                    ? 'bg-[#8b5cf6]/15 border-[#8b5cf6]/40'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                }`}
                title="Rewards"
                aria-label="Rewards"
              >
                <Coins className="w-5 h-5 text-[#8b5cf6] flex-shrink-0" />
                <div className="flex flex-col items-start">
                  <span className="text-xs text-gray-400 leading-tight">Rewards</span>
                  <span className="text-sm font-semibold text-white leading-tight">{rewardBalance.toFixed(2)}</span>
                </div>
              </button>
              {showRewardsPanel && (
                <div className="absolute top-full right-0 mt-2 w-[445px] bg-[#0a0a1a] border border-white/10 rounded-xl shadow-2xl z-[9999] overflow-hidden" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                  <RewardsDropdownPanel balance={null} onClose={() => setShowRewardsPanel(false)} />
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setSideBarMode((s) =>
                    s === sideBarModes.PARTICIPANTS
                      ? null
                      : sideBarModes.PARTICIPANTS
                  );
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Participants"
                aria-label="Participants"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit h-6 sm:h-8">
                  <div className="relative flex items-center justify-center gap-1">
                    <div className="relative flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                      {participantApproveFunc?.length > 0 && (
                        <span className="absolute -top-0.5 -right-1 z-10 inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full bg-red-500 text-white text-[8px] font-semibold shadow-sm">
                          {participantApproveFunc.length}
                        </span>
                      )}
                      <img
                        src="/icons/participants.svg"
                        alt="Participants"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {mMeeting &&
                      mMeeting.participants &&
                      mMeeting.participants.size && (
                        <span
                          className={`text-[#7F7F7F] text-[10px] sm:text-xs font-medium ${
                            participantApproveFunc?.length > 0 ? "ml-0.5" : ""
                          }`}
                        >
                          {mMeeting.participants.size}
                        </span>
                      )}
                  </div>
                </div>
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setShowMore(!showMore);
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Settings"
                aria-label="Settings"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit h-6 sm:h-8">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                    <img
                      src="/icons/settings.svg"
                      alt="Settings"
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                </div>
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setShowMore(!showMore);
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Settings"
                aria-label="Settings"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit h-6 sm:h-8">
                  <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                    <img
                      src="/icons/settings.svg"
                      alt="Settings"
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                </div>
              </button>
            </div>

            {/* Logout button hidden */}

            {/* Profile Menu */}
            <div className="relative">
              <button
                data-profile-button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200 hover:scale-105"
                title="Profile"
                aria-label="Profile"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit h-6 sm:h-8">
                  <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8">
                    {user?.logo ? (
                      <img
                        src={user.logo}
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    ) : (
                      <div
                        className="w-full h-full rounded-full flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${branding?.primaryColor || '#6a1fbf'}, ${branding?.accentColor || '#00c853'})` }}
                      >
                        <span className="text-[8px] sm:text-[10px] font-bold text-white">
                          {user?.name?.[0]?.toUpperCase() || localStorage.getItem("participantName")?.[0]?.toUpperCase() || "G"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <div
                  data-profile-dropdown
                  className="absolute top-full right-0 mt-2 w-48 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl z-[100]"
                >
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="font-medium text-white truncate">{user?.name || localStorage.getItem("participantName") || "Guest"}</p>
                    <p className="text-sm text-gray-500">{meetingRole === "moderator" ? "Host" : meetingRole === "co-host" ? "Co-Host" : "Participant"}</p>
                  </div>
                  <div className="py-1">
                    {isAuthenticated && (
                      <>
                        <a
                          href="/dashboard"
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </a>
                        <a
                          href="/dashboard/profile"
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          Profile & Settings
                        </a>
                      </>
                    )}
                  </div>
                  <div className="border-t border-white/10 py-1">
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        setShowProfileDropdown(false);
                        mMeeting?.leave();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-white/5 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Leave Meeting
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Medium Desktop Screen - Show fewer icons */}
        {!isMobileLike && (
          <div className="hidden md:flex lg:hidden items-center gap-1.5 xl:gap-2">
            {/* Chat icon - HIDDEN for all users (Medium Desktop)
            {meetingSettings && meetingSettings.allowChat && (
              <div className="relative">
                <button
                  onClick={() => {
                    setSideBarMode((s) =>
                      s === sideBarModes.CHAT ? null : sideBarModes.CHAT
                    );
                    setUnreadChat(false);
                  }}
                  className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                  title="Chat"
                  aria-label="Chat"
                >
                  <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                    <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                      <img
                        src="/icons/chat.svg"
                        alt="Chat"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {!compactMode && (
                      <div className="flex flex-col items-center justify-center h-6 opacity-100 transition-all duration-300 ease-in-out">
                        <span className="select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                          Chat
                        </span>
                      </div>
                    )}
                  </div>
                </button>
                {unreadChat && (
                  <div className="pointer-events-none absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full z-20"></div>
                )}
              </div>
            )}
            */}

            {/* Rewards Button - Medium Desktop with Dropdown */}
            <div className="relative menu-container">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRewardsPanel(!showRewardsPanel);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                  showRewardsPanel
                    ? 'bg-[#8b5cf6]/15 border-[#8b5cf6]/40'
                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                }`}
                title="Rewards"
                aria-label="Rewards"
              >
                <Coins className="w-5 h-5 text-[#8b5cf6] flex-shrink-0" />
                <div className="flex flex-col items-start">
                  <span className="text-xs text-gray-400 leading-tight">Rewards</span>
                  <span className="text-sm font-semibold text-white leading-tight">{rewardBalance.toFixed(2)}</span>
                </div>
              </button>
              {showRewardsPanel && (
                <div className="absolute top-full right-0 mt-2 w-[445px] bg-[#0a0a1a] border border-white/10 rounded-xl shadow-2xl z-[9999] overflow-hidden" style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                  <RewardsDropdownPanel balance={null} onClose={() => setShowRewardsPanel(false)} />
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setShowToolsDropdown(!showToolsDropdown);
                  setShowCaptionsDropdown(false);
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Tools"
                aria-label="Tools"
              >
                <div className="h-full flex items-center gap-1.5 transition-all duration-300 ease-in-out min-w-fit relative">
                  {/* Icon and Label Container */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                      <img
                        src="/icons/tools.svg"
                        alt="Tools"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {!compactMode && (
                      <div className="flex flex-col items-center justify-center h-6 opacity-100 transition-all duration-300 ease-in-out">
                        <span className="select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                          Tools
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Chevron - Right side, vertically centered */}
                  {!compactMode && (
                    <div className="flex items-center justify-center opacity-100 transition-all duration-300 ease-in-out">
                      <img
                        src="/icons/chevronDown.svg"
                        alt="dropdown"
                        className={`transition-transform duration-200 ${
                          showToolsDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  )}
                </div>
              </button>

              {/* Tools Options */}
              {showToolsDropdown && (
                <ToolsDropdown
                  meetingRole={meetingRole}
                  startWhiteboard={startWhiteboard}
                  isLiveStreaming={isLiveStreaming}
                  handleStopLiveStream={handleStopLiveStream}
                  setShowLiveStreamModal={setShowLiveStreamModal}
                  setShowToolsDropdown={setShowToolsDropdown}
                  showCaptionsDropdown={showCaptionsDropdown}
                  setShowCaptionsDropdown={setShowCaptionsDropdown}
                  setShowPollsModal={setShowPollsModal}
                  setShowLayoutDropdown={setShowLayoutDropdown}
                  setShowVideoDropdown={setShowVideoDropdown}
                  setShowAudioInputDropdown={setShowAudioInputDropdown}
                  user={user}
                  mMeeting={mMeeting}
                  isMobile={isMobile}
                  meetingSettings={meetingSettings}
                  translationEnabled={translationEnabled}
                  setTranslationEnabled={setTranslationEnabled}
                  translationStatus={translationStatus}
                  startTranslation={startTranslation}
                  changeTranslationLanguage={changeTranslationLanguage}
                  selectedLanguage={selectedLanguage}
                  setSelectedLanguage={setSelectedLanguage}
                  showLiveStreamModal={showLiveStreamModal}
                  liveStreamPlatforms={liveStreamPlatforms}
                  setLiveStreamPlatforms={setLiveStreamPlatforms}
                  streamingCredentials={streamingCredentials}
                  setStreamingCredentials={setStreamingCredentials}
                  handleStartLiveStream={handleStartLiveStream}
                />
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setSideBarMode((s) =>
                    s === sideBarModes.PARTICIPANTS
                      ? null
                      : sideBarModes.PARTICIPANTS
                  );
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Participants"
                aria-label="Participants"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                  <div className="relative flex items-center justify-center gap-1">
                    <div className="relative flex items-center justify-center w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4">
                      {participantApproveFunc?.length > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 z-10 inline-flex items-center justify-center min-w-[14px] h-3.5 px-1 rounded-full bg-red-500 text-white text-[9px] font-semibold shadow-sm">
                          {participantApproveFunc.length}
                        </span>
                      )}
                      <img
                        src="/icons/participants.svg"
                        alt="Participants"
                        className="object-contain hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    </div>
                    {mMeeting &&
                      mMeeting.participants &&
                      mMeeting.participants.size && (
                        <span
                          className={`text-[#7F7F7F] text-[10px] sm:text-xs font-medium ${
                            participantApproveFunc?.length > 0 ? "ml-0.5" : ""
                          }`}
                        >
                          {mMeeting.participants.size}
                        </span>
                      )}
                  </div>
                  {!compactMode && (
                    <div className="flex flex-col items-center justify-center h-6 opacity-100 transition-all duration-300 ease-in-out">
                      <span className="select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                        Participants
                      </span>
                    </div>
                  )}
                </div>
              </button>
            </div>

            <div className="relative">
              <button
                onClick={() => {
                  setShowMore(!showMore);
                }}
                className="px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200"
                title="Settings"
                aria-label="Settings"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                  <div className="flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-5 lg:h-5">
                    <img
                      src="/icons/settings.svg"
                      alt="Settings"
                      className="object-contain hover:opacity-80 transition-all duration-200"
                      draggable={false}
                    />
                  </div>
                  {/* Settings text hidden */}
                </div>
              </button>
            </div>

            {/* Logout button hidden */}

            {/* Profile Menu - Now visible on medium screens */}
            <div className="relative">
              <button
                data-profile-button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className={`px-0.5 sm:px-1 md:px-2 lg:px-3 text-slate-300 h-full hover:text-white flex items-center justify-center transition-all duration-200 hover:scale-105`}
                title="Profile"
                aria-label="Profile"
              >
                <div className="flex flex-col items-center justify-center transition-all duration-300 ease-in-out min-w-fit">
                  <div
                    className={`flex items-center justify-center ${
                      compactMode
                        ? "w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 xl:w-8 xl:h-8"
                        : "w-4 h-4 md:w-7 md:h-7"
                    }`}
                  >
                    {user?.logo ? (
                      <img
                        src={user.logo}
                        alt="Profile"
                        className="w-full h-full rounded-full object-cover hover:opacity-80 transition-all duration-200"
                        draggable={false}
                      />
                    ) : (
                      <div
                        className="w-full h-full rounded-full flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${branding?.primaryColor || '#6a1fbf'}, ${branding?.accentColor || '#00c853'})` }}
                      >
                        <span className="text-[8px] sm:text-[10px] font-bold text-white">
                          {user?.name?.[0]?.toUpperCase() || localStorage.getItem("participantName")?.[0]?.toUpperCase() || "G"}
                        </span>
                      </div>
                    )}
                  </div>
                  {!compactMode && (
                    <span className="mt-1 duration-300 ease-in-out transition-all select-none text-[8px] sm:text-[10px] text-[#E0E0E0] whitespace-nowrap">
                      Profile
                    </span>
                  )}
                </div>
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown && (
                <div
                  data-profile-dropdown
                  className="absolute top-full right-0 mt-2 w-48 bg-[#1a1a2e] border border-white/10 rounded-lg shadow-2xl z-[100]"
                >
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="font-medium text-white truncate">{user?.name || localStorage.getItem("participantName") || "Guest"}</p>
                    <p className="text-sm text-gray-500">{meetingRole === "moderator" ? "Host" : meetingRole === "co-host" ? "Co-Host" : "Participant"}</p>
                  </div>
                  <div className="py-1">
                    {isAuthenticated && (
                      <>
                        <a
                          href="/dashboard"
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          Dashboard
                        </a>
                        <a
                          href="/dashboard/profile"
                          target="_blank"
                          rel="noopener noreferrer"
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          Profile & Settings
                        </a>
                      </>
                    )}
                  </div>
                  <div className="border-t border-white/10 py-1">
                    <button
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        setShowProfileDropdown(false);
                        mMeeting?.leave();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-white/5 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Leave Meeting
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Bar - DISABLED: StudioBottomControls handles bottom controls on mobile */}
      {/* <MobileBottomBar
        isMobileLike={isMobileLike}
        isMobileLandscape={isMobileLandscape}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        setShowMobilePanel={setShowMobilePanel}
        localMicOn={localMicOn}
        handleToggleMic={handleToggleMic}
        showMicDropdown={showMicDropdown}
        mics={mics}
        selectedMic={selectedMic}
        changeMic={changeMic}
        showAudioInputDropdown={showAudioInputDropdown}
        setShowAudioInputDropdown={setShowAudioInputDropdown}
        speakers={speakers}
        selectedSpeaker={selectedSpeaker}
        setSelectedSpeaker={setSelectedSpeaker}
        showSpeakerDropdown={showSpeakerDropdown}
        setShowSpeakerDropdown={setShowSpeakerDropdown}
        getMics={getMics}
        setShowMicDropdown={setShowMicDropdown}
        localWebcamOn={localWebcamOn}
        localWebcamState={localWebcamState}
        setLocalWebcamState={setLocalWebcamState}
        mMeeting={mMeeting}
        showVideoDropdown={showVideoDropdown}
        webcams={webcams}
        selectedWebcam={selectedWebcam}
        changeWebcam={changeWebcam}
        showVideoSourceDropdown={showVideoSourceDropdown}
        setShowVideoSourceDropdown={setShowVideoSourceDropdown}
        setShowVirtualBackgroundModal={setShowVirtualBackgroundModal}
        getWebcams={getWebcams}
        setShowVideoDropdown={setShowVideoDropdown}
        setSideBarMode={setSideBarMode}
        unreadChat={unreadChat}
        setUnreadChat={setUnreadChat}
        setShowHelpModal={setShowHelpModal}
        handleLeaveModal={handleLeaveModal}
      /> */}

      {/* Mobile Live Stream Modal */}
      {isMobileLike && showLiveStreamModal && createPortal(
        <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowLiveStreamModal(false)}
          />
          <div className="absolute left-0 right-0 bg-white/10 backdrop-blur-xl border-t border-white/15 rounded-t-2xl p-5 text-white max-h-[80vh] overflow-y-auto bottom-20">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <img
                  src="/icons/livestream.svg"
                  alt="Livestream"
                  className="w-5 h-5"
                />
                <h3 className="text-base font-medium">Livestream</h3>
              </div>
              <button
                onClick={() => setShowLiveStreamModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="h-px bg-gray-700 mb-4" />
            <div className="text-center text-xs tracking-widest text-gray-300 uppercase mb-3">
              Select your platform
            </div>

            <div className="space-y-4">
              {/* YouTube */}
              <div className="space-y-2 pb-3 border-b border-white/10">
                <div className="flex items-center justify-start p-1">
                  <label className="relative inline-flex items-center cursor-pointer mr-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={liveStreamPlatforms.youtube}
                      onChange={(e) =>
                        setLiveStreamPlatforms((prev) => ({
                          ...prev,
                          youtube: e.target.checked,
                        }))
                      }
                    />
                    <div className="w-9 h-5 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                  <div className="flex items-center gap-2">
                    <img
                      src="/icons/yt.svg"
                      alt="YouTube"
                      className="w-7 h-7"
                    />
                    <div className="font-medium">YouTube</div>
                  </div>
                </div>
                <div
                  className={`${
                    liveStreamPlatforms.youtube
                      ? "opacity-100 max-h-40"
                      : "opacity-0 max-h-0 pointer-events-none"
                  } transition-all duration-300 ease-out pl-12 space-y-2 overflow-hidden`}
                >
                  <input
                    type="text"
                    placeholder="Stream URL"
                    value={streamingCredentials.youtube.url}
                    onChange={(e) =>
                      setStreamingCredentials((prev) => ({
                        ...prev,
                        youtube: { ...prev.youtube, url: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-red-600"
                  />
                  <input
                    type="text"
                    placeholder="Stream Key"
                    value={streamingCredentials.youtube.key}
                    onChange={(e) =>
                      setStreamingCredentials((prev) => ({
                        ...prev,
                        youtube: { ...prev.youtube, key: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-red-600"
                  />
                </div>
              </div>

              {/* Facebook */}
              <div className="space-y-2 pb-3 border-b border-white/10">
                <div className="flex items-center justify-start p-1">
                  <label className="relative inline-flex items-center cursor-pointer mr-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={liveStreamPlatforms.facebook}
                      onChange={(e) =>
                        setLiveStreamPlatforms((prev) => ({
                          ...prev,
                          facebook: e.target.checked,
                        }))
                      }
                    />
                    <div className="w-9 h-5 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <div className="flex items-center gap-2">
                    <img
                      src="/icons/fb.svg"
                      alt="Facebook"
                      className="w-7 h-7"
                    />
                    <div className="font-medium">Facebook</div>
                  </div>
                </div>
                <div
                  className={`${
                    liveStreamPlatforms.facebook
                      ? "opacity-100 max-h-40"
                      : "opacity-0 max-h-0 pointer-events-none"
                  } transition-all duration-300 ease-out pl-12 space-y-2 overflow-hidden`}
                >
                  <input
                    type="text"
                    placeholder="Stream URL"
                    value={streamingCredentials.facebook.url}
                    onChange={(e) =>
                      setStreamingCredentials((prev) => ({
                        ...prev,
                        facebook: { ...prev.facebook, url: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-blue-600"
                  />
                  <input
                    type="text"
                    placeholder="Stream Key"
                    value={streamingCredentials.facebook.key}
                    onChange={(e) =>
                      setStreamingCredentials((prev) => ({
                        ...prev,
                        facebook: { ...prev.facebook, key: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-blue-600"
                  />
                </div>
              </div>

              {/* LinkedIn */}
              <div className="space-y-2">
                <div className="flex items-center justify-start p-1">
                  <label className="relative inline-flex items-center cursor-pointer mr-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={liveStreamPlatforms.linkedin}
                      onChange={(e) =>
                        setLiveStreamPlatforms((prev) => ({
                          ...prev,
                          linkedin: e.target.checked,
                        }))
                      }
                    />
                    <div className="w-9 h-5 bg-gray-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-700"></div>
                  </label>
                  <div className="flex items-center gap-2">
                    <img
                      src="/icons/linkedin.svg"
                      alt="LinkedIn"
                      className="w-7 h-7"
                    />
                    <div className="font-medium">LinkedIn</div>
                  </div>
                </div>
                <div
                  className={`${
                    liveStreamPlatforms.linkedin
                      ? "opacity-100 max-h-40"
                      : "opacity-0 max-h-0 pointer-events-none"
                  } transition-all duration-300 ease-out pl-12 space-y-2 overflow-hidden`}
                >
                  <input
                    type="text"
                    placeholder="Stream URL"
                    value={streamingCredentials.linkedin.url}
                    onChange={(e) =>
                      setStreamingCredentials((prev) => ({
                        ...prev,
                        linkedin: { ...prev.linkedin, url: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-blue-700"
                  />
                  <input
                    type="text"
                    placeholder="Stream Key"
                    value={streamingCredentials.linkedin.key}
                    onChange={(e) =>
                      setStreamingCredentials((prev) => ({
                        ...prev,
                        linkedin: { ...prev.linkedin, key: e.target.value },
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-white/20 rounded text-white text-sm focus:outline-none focus:border-blue-700"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-5">
              <button
                className="px-4 py-2 rounded-lg w-1/2 bg-transparent border border-white text-white hover:bg-white/10"
                onClick={() => setShowLiveStreamModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg w-1/2 bg-transparent border border-[#00C853] text-[#00C853] hover:bg-white/10"
                onClick={() => handleStartLiveStream(liveStreamPlatforms)}
              >
                Go Live
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Mobile Swipe-Up Panel */}
      <MobileSwipeUpPanel
        showMobilePanel={showMobilePanel}
        isMobileLike={isMobileLike}
        isMobileLandscape={isMobileLandscape}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        setShowMobilePanel={setShowMobilePanel}
        setShowCaptionsDropdown={setShowCaptionsDropdown}
        showCaptionsDropdown={showCaptionsDropdown}
        setShowReactions={setShowReactions}
        showReactions={showReactions}
        setShowToolsDropdown={setShowToolsDropdown}
        setShowLayoutDropdown={setShowLayoutDropdown}
        meetingRole={meetingRole}
        mMeeting={mMeeting}
        handleRecordingClick={handleRecordingClick}
        meetingSettings={meetingSettings}
        publishRaiseHand={publishRaiseHand}
        showMore={showMore}
        setShowMore={setShowMore}
        setShowHelpModal={setShowHelpModal}
        translationEnabled={translationEnabled}
        setTranslationEnabled={setTranslationEnabled}
        stopTranslation={stopTranslation}
        startTranslation={startTranslation}
        selectedLanguage={selectedLanguage}
        changeTranslationLanguage={changeTranslationLanguage}
        setSelectedLanguage={setSelectedLanguage}
        showElementSection={showElementSection}
        setShowElementSection={setShowElementSection}
        setShowPollsModal={setShowPollsModal}
        handleEmojiClick={handleEmojiClick}
        // Live stream controls for mobile panel
        setShowLiveStreamModal={setShowLiveStreamModal}
        isLiveStreaming={isLiveStreaming}
        handleStopLiveStream={handleStopLiveStream}
        setShowCaptionTranslationPanel={setShowCaptionTranslationPanel}
      />

      {/* Help & Support Modal */}
      {showHelpModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowHelpModal(false)}
          />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-8 w-full max-w-md mx-4 text-white shadow-2xl">
            <h3 className="text-xl font-semibold text-center mb-6">
              Share Feedback
            </h3>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-[#cfcfcf] uppercase mb-2 tracking-wide">
                  Please Share Your Feedback
                </p>
                <textarea
                  value={helpIssueText}
                  onChange={(e) => setHelpIssueText(e.target.value)}
                  placeholder="Tell us what your experience was.."
                  className="w-full h-28 resize-none bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#6a1fbf]"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-center mt-6">
              <button
                className="px-6 py-2.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-colors"
                onClick={() => {
                  setShowHelpModal(false);
                  setHelpIssueText("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2.5 rounded-full bg-[#6a1fbf] text-white text-sm hover:bg-[#5a19a0] transition-colors"
                onClick={handleHelpSubmit}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Settings Panel - Single render for all screen sizes */}
      <SettingsPanel
        show={showMore}
        compactMode={compactMode}
        isMobileLike={isMobileLike}
        setShowHelpModal={setShowHelpModal}
        setShowMore={setShowMore}
        handleToggleFullscreen={handleToggleFullscreen}
        handleCopyRoomLink={handleCopyRoomLink}
        user={user}
        TranscriptionToggleRow={TranscriptionToggleRow}
        WaitingRoomToggleRow={WaitingRoomToggleRow}
        GuestCanUnmuteToggleRow={GuestCanUnmuteToggleRow}
        ApprovalSoundToggleRow={ApprovalSoundToggleRow}
        meetingRole={meetingRole}
      />

      {/* Caption Translation Panel - Similar to Settings Panel */}
      <CaptionTranslationPanel
        show={showCaptionTranslationPanel}
        isMobileLike={isMobileLike}
        setShowCaptionTranslationPanel={setShowCaptionTranslationPanel}
        translationEnabled={translationEnabled}
        setTranslationEnabled={setTranslationEnabled}
        stopTranslation={stopTranslation}
        startTranslation={startTranslation}
        selectedLanguage={selectedLanguage}
        changeTranslationLanguage={changeTranslationLanguage}
        setSelectedLanguage={setSelectedLanguage}
        translationStatus={translationStatus}
      />

      {/* Virtual Background Modal */}
      {showVirtualBackgroundModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          {/* Overlay */}
          <div
            className="absolute inset-0"
            onClick={() => setShowVirtualBackgroundModal(false)}
          ></div>

          {/* Modal Content */}
          <div className="fixed left-0 top-[80px] bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-white/15">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/20">
              <h3 className="text-lg font-semibold text-white">
                Choose Virtual Background
              </h3>
              <button
                onClick={() => setShowVirtualBackgroundModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Blur Controls */}
            <div className="p-4 border-b border-white/20">
              <div className="space-y-4">
                {/* Blur Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium">
                    BLUR YOUR BACKGROUND
                  </span>
                  <div className="flex bg-gray-700 p-1 rounded">
                    <button
                      onClick={() => setBlurEnabled(false)}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        !blurEnabled
                          ? "bg-[#7700FF] text-white"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      OFF
                    </button>
                    <button
                      onClick={() => setBlurEnabled(true)}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        blurEnabled
                          ? "bg-[#7700FF] text-white"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      ON
                    </button>
                  </div>
                </div>

                {/* Blur Strength Slider */}
                {blurEnabled && (
                  <div className="space-y-2">
                    <span className="text-white text-sm font-medium">
                      Blur Strength
                    </span>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={blurStrength}
                        onChange={(e) =>
                          setBlurStrength(parseInt(e.target.value))
                        }
                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                        style={{
                          background: `linear-gradient(to right, #7700FF 0%, #7700FF ${blurStrength}%, #374151 ${blurStrength}%, #374151 100%)`,
                        }}
                      />
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs font-medium">
                        {blurStrength}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Background Options */}
            <div className="p-4 h-[60vh] overflow-y-auto">
              <div className="space-y-3">
                <span className="text-white text-sm font-medium">
                  BLUR YOUR BACKGROUND
                </span>
                <div className="grid grid-cols-2 gap-3">
                  {/* No Background Option */}
                  <button
                    onClick={() => {
                      handleStartVirtualBackground(null);
                      setShowVirtualBackgroundModal(false);
                    }}
                    className={`p-4 rounded-lg border transition-colors ${
                      !virtualBackgroundOn
                        ? "border-[#7700FF] bg-[#7700FF]/10"
                        : "border-white/20 hover:border-gray-500"
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                      </div>
                      <span className="text-white text-xs text-center">
                        No Background
                      </span>
                    </div>
                  </button>

                  {/* Upload Image Option */}
                  {/* <button className="p-4 rounded-lg border-2 border-dashed border-[#7700FF] hover:border-[#7700FF]/80 transition-colors">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-[#7700FF]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </div>
                      <span className="text-white text-xs text-center">
                        UPLOAD YOUR IMAGE
                      </span>
                    </div>
                  </button> */}
                </div>

                {/* Background Images Grid */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {backgroundImageUrls.map((background, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        handleStartVirtualBackground(background);
                        setShowVirtualBackgroundModal(false);
                      }}
                      className={`rounded-lg overflow-hidden transition-colors ${
                        virtualBackgroundOn && selectedBackground === background
                          ? "ring-2 ring-[#7700FF]"
                          : "hover:ring-2 hover:ring-gray-500"
                      }`}
                    >
                      <img
                        className="w-full h-20 object-cover"
                        src={background}
                        alt={`Background ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Start Recording Confirmation — removed: new multi-layout recording
          captures all layouts during the session and lets viewers pick one at
          playback time, so the upfront layout picker is no longer needed. */}

      {/* Recording consent notice for participants */}
      {showRecordingConsentModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowRecordingConsentModal(false)}
          />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-8 w-full max-w-md mx-4 text-white shadow-2xl">
            <h3 className="text-xl font-semibold text-center mb-3">
              This session is being recorded
            </h3>
            <p className="text-sm text-[#cfcfcf] mb-8 text-center">
              If you stay, you're giving consent to record audio and video.
              <br />
              Don't want to be recorded? You can leave anytime.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                className="px-6 py-2.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-colors"
                onClick={handleLeaveConfirm}
              >
                Leave Meeting
              </button>
              <button
                className="px-6 py-2.5 rounded-full bg-[#6a1fbf] text-white text-sm hover:bg-[#5a19a0] transition-colors"
                onClick={() => setShowRecordingConsentModal(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {showLeaveModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowLeaveModal(false)}
          />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-8 w-full max-w-md mx-4 text-white shadow-2xl">
            <h3 className="text-xl font-semibold text-center mb-3">Leave Room?</h3>
            <p className="text-sm text-[#cfcfcf] text-center mb-8">
              You're about to leave this room. Choose how you'd like to proceed:
            </p>

            <div className="flex flex-col gap-3">
              <button
                className="w-full px-6 py-2.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-colors"
                onClick={() => setShowLeaveModal(false)}
              >
                Stay in Room
              </button>
              <button
                className="w-full px-6 py-2.5 rounded-full bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                onClick={handleLeaveConfirm}
              >
                Leave Room
              </button>
              {isHostOrCoHost && (
                <button
                  className="w-full px-6 py-2.5 rounded-full border border-red-500/50 text-red-400 text-sm hover:bg-red-600/10 transition-colors"
                  onClick={handleEndForAll}
                >
                  End Session for All
                </button>
              )}
            </div>
          </div>
        </div>
      , document.body)}

      <style>{`@keyframes floatUp{0%{transform:translateY(0) translateX(0) scale(1);opacity:1}25%{transform:translateY(-25vh) translateX(20px) scale(1.1);opacity:.85}50%{transform:translateY(-50vh) translateX(0) scale(1.15);opacity:.7}75%{transform:translateY(-75vh) translateX(-20px) scale(1.1);opacity:.5}100%{transform:translateY(-100vh) translateX(0) scale(1);opacity:0}}`}</style>
      <div
        className="pointer-events-none fixed"
        style={{ bottom: 72, left: "7%", zIndex: 1302 }}
      >
        {recentReactions.slice(-6).map((r) => (
          <div
            key={r.id}
            className="mt-1 flex flex-col items-center"
            style={{
              animation: "floatUp 6s ease-out forwards",
              filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.2))",
            }}
          >
            <div className="text-[28px] mb-0.5">{r.emoji}</div>
            <div className="mt-0.5 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px]">
              {/* {getParticipantName(r.from)} */}
              {r.from}
            </div>
          </div>
        ))}
      </div>

      {/* Polls Modal */}
      {showPollsModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowPollsModal(false)}
          />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-8 w-full max-w-lg mx-4 text-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-center mb-6">
              Select a Poll to Launch
            </h3>

            <div className="space-y-4 mb-6">
              <div className="relative">
                <select
                  value={selectedPoll?.id || ""}
                  onChange={(e) => {
                    const pollId = e.target.value;
                    const poll = polls.find((p) => p.id === pollId);
                    setSelectedPoll(poll || null);
                  }}
                  className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white text-sm focus:outline-none focus:border-[#6a1fbf] appearance-none"
                  disabled={loadingPolls}
                >
                  <option value="">
                    {loadingPolls ? "Loading polls..." : "Select a Poll"}
                  </option>
                  {polls.map((poll) => (
                    <option key={poll.id} value={poll.id}>
                      {poll.question || "Untitled Poll"}
                    </option>
                  ))}
                </select>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white pointer-events-none"
                >
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </div>

              {selectedPoll && (
                <div className="p-4 bg-white/5 border border-white/15 rounded-xl">
                  <div className="text-white font-medium mb-2">
                    {selectedPoll.question}
                  </div>
                  {selectedPoll.options && selectedPoll.options.length > 0 && (
                    <div className="space-y-2">
                      {selectedPoll.options.map((option, index) => (
                        <div key={index} className="text-[#cfcfcf] text-sm pl-4">
                          • {option.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <button
                className="px-6 py-2.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-colors"
                onClick={() => {
                  setShowPollsModal(false);
                  setSelectedPoll(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2.5 rounded-full bg-[#6a1fbf] text-white text-sm hover:bg-[#5a19a0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                onClick={handleLaunchPoll}
                disabled={!selectedPoll || loadingPolls}
              >
                Launch Poll
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Poll Results Modal */}
      {showPollResultsModal && pollResults && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowPollResultsModal(false)}
          />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-8 w-full max-w-lg mx-4 text-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-center mb-6">
              Poll Results
            </h3>

            <div className="mb-6">
              <p className="text-white text-base font-medium text-center">
                {pollResults.question}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {pollResults.options &&
                pollResults.options.map((option, index) => {
                  const totalVotes = pollResults.votes?.length || 0;
                  const percentage =
                    totalVotes > 0
                      ? Math.round((option.votes / totalVotes) * 100)
                      : 0;

                  return (
                    <div
                      key={index}
                      className="p-4 rounded-xl border border-white/15 bg-white/5"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-sm font-medium">
                          {option.text}
                        </span>
                        <span className="text-[#cfcfcf] text-sm">
                          {percentage}%
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2.5">
                        <div
                          className="bg-[#6a1fbf] h-2.5 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <p className="text-[#cfcfcf] text-xs mt-2">
                        {option.votes} vote{option.votes !== 1 ? "s" : ""}
                      </p>

                      {pollResults.settings?.showParticipantsWhoVoted &&
                        !pollResults.settings?.anonymousVoting && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-[#cfcfcf] text-xs mb-2">
                              Voted by:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {pollResults.votes
                                ?.filter((vote) =>
                                  vote.optionIndices.includes(index)
                                )
                                .map((vote, voteIndex) => (
                                  <span
                                    key={voteIndex}
                                    className="text-xs bg-white/10 px-2 py-1 rounded-full"
                                  >
                                    {vote.participantName}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}
            </div>

            <div className="text-center mb-4">
              <p className="text-[#cfcfcf] text-sm">
                Total Votes: {pollResults.votes?.length || 0}
              </p>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setShowPollResultsModal(false)}
                className="px-6 py-2.5 rounded-full bg-[#6a1fbf] text-white text-sm hover:bg-[#5a19a0] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Profile Confirmation Modal */}
      {showProfileConfirmModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#001233]/80 to-[#6a1fbf]/50 backdrop-blur-md">
          <div
            className="absolute inset-0"
            onClick={() => setShowProfileConfirmModal(false)}
          />
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-8 w-full max-w-md mx-4 text-white shadow-2xl">
            <h3 className="text-xl font-semibold text-center mb-3">
              Go to Profile?
            </h3>
            <p className="text-sm text-[#cfcfcf] text-center mb-8">
              You're about to open your profile page. This will open in a new tab.
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => setShowProfileConfirmModal(false)}
                className="w-full px-6 py-2.5 rounded-full border border-white/30 text-white text-sm hover:bg-white/10 transition-colors"
              >
                Stay in Room
              </button>
              <button
                onClick={() => {
                  setShowProfileConfirmModal(false);
                  window.open("/dashboard/profile", "_blank");
                }}
                className="w-full px-6 py-2.5 rounded-full bg-[#6a1fbf] text-white text-sm hover:bg-[#5a19a0] transition-colors"
              >
                Go to Profile
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Recording start hold-overlay — Loom-style 3-2-1 countdown that
          continues with a "Connecting recorder…" spinner until the worker
          confirms ffmpeg is actually capturing. Without the post-countdown
          hold, users start speaking during the worker's launch race
          (RECORDING_STARTED fires when the API queues the job, not when
          frames flow), losing the first 6-8 seconds of every recording. */}
      {(recordingCountdown !== null || recordingWaitingForStart) && createPortal(
        <div
          key={recordingCountdown !== null ? `rec-countdown-${recordingCountdown}` : 'rec-waiting'}
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none bg-black/40 backdrop-blur-sm"
          style={recordingCountdown !== null ? { animation: 'rlinkRecCountdownFade 1s ease-out forwards' } : undefined}
        >
          {recordingCountdown !== null ? (
            <div
              className="flex flex-col items-center justify-center"
              style={{ animation: 'rlinkRecCountdownPop 1s ease-out forwards' }}
            >
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-500/30 blur-2xl scale-150" />
                <div
                  className="relative w-44 h-44 rounded-full flex items-center justify-center text-white font-bold border-4 border-white/30 shadow-2xl"
                  style={{
                    fontSize: '7rem',
                    background: 'radial-gradient(circle at center, #6a1fbf 0%, #001233 80%)',
                    textShadow: '0 4px 24px rgba(0,0,0,0.5)',
                  }}
                >
                  {recordingCountdown}
                </div>
              </div>
              <div className="mt-6 text-white/90 text-lg font-medium tracking-wide uppercase">
                Recording starts in&hellip;
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-6 text-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-500/30 blur-2xl scale-150" />
                <div
                  className="relative w-44 h-44 rounded-full flex items-center justify-center border-4 border-white/30 shadow-2xl"
                  style={{ background: 'radial-gradient(circle at center, #6a1fbf 0%, #001233 80%)' }}
                >
                  <svg
                    className="w-20 h-20 text-white animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
                  </svg>
                </div>
              </div>
              <div className="mt-6 text-white text-lg font-semibold tracking-wide uppercase">
                Connecting recorder&hellip;
              </div>
              <div className="mt-2 text-white/60 text-xs max-w-sm">
                Hold a moment — the recorder is attaching. Speak after this overlay clears so the start of your meeting isn't cut off.
              </div>
            </div>
          )}
          <style>{`
            @keyframes rlinkRecCountdownPop {
              0%   { transform: scale(0.6); opacity: 0; }
              30%  { transform: scale(1.15); opacity: 1; }
              60%  { transform: scale(1.0); opacity: 1; }
              100% { transform: scale(1.05); opacity: 0; }
            }
            @keyframes rlinkRecCountdownFade {
              0%   { opacity: 0; }
              20%  { opacity: 1; }
              80%  { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
        </div>,
        document.body
      )}

      <PrivacyNoticeModal
        open={!!privacyNoticeOpen}
        onClose={() => setPrivacyNoticeOpen(false)}
        onDisablePrivacy={handleDisablePrivacy}
        disablePrivacyDisabled={!isHostOrCoHost}
      />
    </div>
  );
};

export default TopBar;
